import { BLOG_RELATED_SLUGS } from './blogRelated';
import {
  ANCHOR_PRICE_USD,
  L1_MONTHLY_USD,
  currentPassPriceUsd,
  currentL2PassPriceUsd,
  freeMockPublicLine,
  freeTrialCtaLabel,
} from '../constants/pricing';

const FREE_MOCK = freeMockPublicLine();
const TRIAL_CTA = { label: freeTrialCtaLabel(), href: '/quiz/session-1' };
const ANNUAL_PRICING_CTA = {
  label: `See lifetime access — $${currentPassPriceUsd()} one-time`,
  href: '/pricing',
};
const L2_PRICE = currentL2PassPriceUsd();
const L2_PRICING_CTA = {
  label: `Unlock Level 2 lifetime — $${L2_PRICE}`,
  href: '/level/2',
};
const L3_PRICE = L2_PRICE;
const L3_PRICING_CTA = {
  label: `Unlock Level 3 lifetime — $${L3_PRICE}`,
  href: '/level/3',
};
const PACKAGE_PRICING_CTA = {
  label: 'Get L1+L2+L3 lifetime — $349',
  href: '/pricing',
};

/** Practice pricing in post copy — list prices from src/constants/pricing.js. */
const L1_PRICE_LINE = `$${L1_MONTHLY_USD}/month or $${ANCHOR_PRICE_USD} lifetime`;

/**
 * CFA Institute sources for fees and deadlines (checked 2026-10-23).
 * - Pricing announcement: 2026 fees, enrollment fee removed 29 Apr 2025, applies to exams
 *   "seated in February 2026 and beyond".
 * - Dates & fees: USD 250 reschedule, USD 3,520–4,570 for all three levels, taxes excluded,
 *   missed-appointment fees nonrefundable; its exam calendar (served from
 *   https://www.cfainstitute.org/api/1/cfa_exams) lists every Level I window and deadline below.
 */
const CFA_DATES_FEES_URL = 'https://www.cfainstitute.org/programs/cfa-program/dates-fees';
const CFA_L1_SAMPLES_URL = 'https://www.cfainstitute.org/programs/cfa-program/cfa-program-level-i-sample-questions';
/** Exam format, 10-year average pass rates and results timing (docs/SEO_BLOG_CTR_1028.md). */
const CFA_EXAM_URL = 'https://www.cfainstitute.org/programs/cfa-program/exam';
const CFA_EXAM_RESULTS_URL = 'https://www.cfainstitute.org/programs/cfa-program/candidate-resources/exam-results';
const CFA_PASS_RATES_URL =
  'https://www.cfainstitute.org/sites/default/files/docs/programs/cfa-program/candidate-resources/cfa-exam-results-pass-rate-infographic.pdf';

/** Level I 2027 windows from CFA Institute's dates & fees calendar ("passed" as of 2026-10-28). */
const L1_2027_WINDOWS_TABLE = {
  caption: `Source: [CFA Institute dates & fees](${CFA_DATES_FEES_URL}), Level I. All deadlines are 11:59 PM ET. Registration for August 2027 opens 3 Nov 2026.`,
  head: ['Level I window', 'Exam dates', 'Early deadline (USD 1,140)', 'Registration closes (USD 1,490)'],
  rows: [
    ['February 2027', '22–28 Feb 2027', '7 Jul 2026 (passed)', '5 Nov 2026'],
    ['May 2027', '11–17 May 2027', '14 Oct 2026 (passed)', '10 Feb 2027'],
    ['August 2027', '15–22 Aug 2027', '20 Jan 2027', '29 Apr 2027'],
  ],
};

/** Conversion landing — sit complimentary Mock 1. */
export const MONEY_BLOG_SLUG = 'free-cfa-level-1-mock-exam-2026';

/** Level 2 conversion landing — paid item-set mocks. */
export const L2_MONEY_BLOG_SLUG = 'cfa-level-2-mock-exam-2026';

/** Level 3 conversion landing — mixed item-set + constructed response. */
export const L3_MONEY_BLOG_SLUG = 'cfa-level-3-mock-exam-2026';

/**
 * SEO landing post — practice/sample-questions guide (separate from the free-mock money page).
 * The former cfa-level-1-practice-questions-2026 twin 301s here (public/_redirects).
 */
export const WEAPON_BLOG_SLUG = 'cfa-level-1-practice-questions-free';

/** Timely Level 1 registration post (February 2027 window).
 * The former cfa-level-1-registration-fee-cost-2026 post 301s to /cfa-level-1-exam-fees/ (public/_redirects). */
export const FEB_2027_BLOG_SLUG = 'cfa-level-1-february-2027-deadlines-fees';

/** Focus keywords for meta / schema (per post). */
const BLOG_KEYWORDS = {
  [MONEY_BLOG_SLUG]: [
    'free CFA Level 1 mock exam',
    'CFA Level 1 mock exam 2026',
    'free CFA mock exam',
    '90 question CFA mock',
    'timed CFA Level 1 mock',
  ],
  [WEAPON_BLOG_SLUG]: [
    'CFA Level 1 sample questions',
    'free CFA Level 1 practice questions',
    'CFA Level 1 practice questions with answers',
    'CFA Level 1 practice test',
    'CFA Level 1 question bank',
  ],
  [FEB_2027_BLOG_SLUG]: [
    'CFA February 2027 registration deadline',
    'CFA Level 1 February 2027',
    'CFA exam dates 2027',
    'CFA Level 1 2027 fees',
  ],
  'how-to-pass-cfa-level-1-study-plan': [
    'how to pass CFA Level 1',
    'CFA Level 1 study plan',
    '300 hours CFA',
    'CFA Level 1 schedule 2026',
  ],
  'cfa-level-1-pass-rate-2026': [
    'CFA Level 1 pass rate',
    'CFA pass rate 2026',
    'CFA Level 1 results',
    'CFA Level 1 difficulty',
  ],
  'cfa-level-1-exam-dates-2026': [
    'CFA Level 1 exam dates 2026',
    'CFA Level 1 November 2026',
    'CFA November 2026 exam dates',
    'CFA exam schedule',
    'CFA Level 1 window',
  ],
  'cfa-level-1-formulas-2026': [
    'CFA Level 1 formulas',
    'CFA formula sheet',
    'Level 1 cheat sheet',
    'CFA Level 1 formula glossary',
  ],
  'cfa-level-1-exam-format-topic-weights-2026': [
    'CFA Level 1 exam format',
    'CFA topic weights',
    'Level 1 structure',
    'CFA Level 1 180 questions',
  ],
  'cfa-level-1-ethics-study-guide': [
    'CFA Level 1 ethics',
    'CFA ethics study',
    'Standards of Professional Conduct',
    'CFA Ethics vignettes',
  ],
  'cfa-level-1-calculator-ba-ii-plus': [
    'CFA calculator',
    'BA II Plus CFA',
    'CFA Level 1 calculator',
    'BA II Plus settings CFA',
  ],
  'cfa-level-1-mock-exam-how-to-practice': [
    'CFA mock exam tips',
    'CFA exam strategy',
    'timed practice CFA',
    'CFA Level 1 pacing',
  ],
  'cfa-level-1-financial-statement-analysis-tips': [
    'CFA Level 1 FSA',
    'financial statement analysis CFA',
    'CFA FRA tips',
    'CFA Level 1 ratios',
  ],
  'cfa-level-1-fixed-income-duration-guide': [
    'CFA duration',
    'fixed income CFA Level 1',
    'Macaulay duration',
    'modified duration CFA',
  ],
  'cfa-level-1-quantitative-methods-tvm': [
    'CFA time value of money',
    'TVM CFA Level 1',
    'quantitative methods CFA',
    'CFA Level 1 statistics',
  ],
  'cfa-level-1-results-release-timeline': [
    'CFA results release',
    'CFA Level 1 results date',
    'when CFA results',
    'CFA results 5-7 weeks',
  ],
  'cfa-level-1-alternative-investments-guide': [
    'CFA alternatives',
    'alternative investments Level 1',
    'hedge funds CFA',
    'CFA private equity Level 1',
  ],
  'cfa-level-1-derivatives-study-guide': [
    'CFA derivatives Level 1',
    'options CFA',
    'forwards and swaps CFA',
    'put-call parity CFA',
  ],
  'cfa-level-1-portfolio-management-capm': [
    'CFA CAPM',
    'portfolio management Level 1',
    'SML CFA',
    'Sharpe ratio CFA',
  ],
  'cfa-level-1-last-month-study-plan': [
    'CFA last month study',
    'CFA cram plan',
    'CFA final review',
    'CFA Level 1 final 4 weeks',
  ],
  'cfa-level-1-vs-level-2-difference': [
    'CFA Level 1 vs Level 2',
    'CFA Level 2 format',
    'CFA charter levels',
    'CFA item sets vs multiple choice',
  ],
  [L2_MONEY_BLOG_SLUG]: [
    'CFA Level 2 mock exam',
    'CFA Level 2 mock exam 2026',
    'CFA L2 item set practice',
    'timed CFA Level 2 mock',
  ],
  [L3_MONEY_BLOG_SLUG]: [
    'CFA Level 3 mock exam',
    'CFA Level 3 mock exam 2026',
    'CFA L3 constructed response practice',
    'timed CFA Level 3 mock',
  ],
  'cfa-level-2-item-sets-explained': [
    'CFA Level 2 item sets',
    'CFA vignette questions',
    'how Level 2 item sets work',
    'CFA L2 case questions',
  ],
  'cfa-level-2-topic-weights-2026': [
    'CFA Level 2 topic weights 2026',
    'CFA L2 curriculum weights',
    'Level 2 exam topic breakdown',
    'CFA Level 2 FRA weight',
  ],
  'how-to-pass-cfa-level-2-study-plan': [
    'how to pass CFA Level 2',
    'CFA Level 2 study plan',
    'CFA L2 study schedule',
    '300 hours CFA Level 2',
  ],
  'cfa-level-2-exam-format-2026': [
    'CFA Level 2 exam format',
    'CFA Level 2 sessions',
    'Level 2 CBT format',
    'CFA L2 question count',
  ],
  'cfa-level-2-exam-dates-2026': [
    'CFA Level 2 exam dates 2026',
    'CFA Level 2 November 2026',
    'CFA L2 exam windows',
    'Level 2 exam schedule 2027',
  ],
  'cfa-level-3-exam-dates-2026': [
    'CFA Level 3 exam dates 2026',
    'CFA Level 3 February 2027',
    'CFA L3 exam windows',
    'Level 3 exam schedule',
  ],
  'cfa-level-3-constructed-response-guide': [
    'CFA Level 3 constructed response',
    'CFA Level 3 essay command words',
    'CFA L3 written response tips',
    'how to answer CFA Level 3 essays',
  ],
  'cfa-level-3-topic-weights-2026': [
    'CFA Level 3 topic weights 2026',
    'CFA Level 3 pathways',
    'Level 3 curriculum weights',
    'CFA L3 asset allocation weight',
  ],
  'how-to-pass-cfa-level-3-study-plan': [
    'how to pass CFA Level 3',
    'CFA Level 3 study plan',
    'CFA L3 study schedule',
    'CFA Level 3 essay practice plan',
  ],
  'cfa-level-2-vs-level-3-difference': [
    'CFA Level 2 vs Level 3',
    'CFA Level 3 format difference',
    'item sets vs constructed response',
    'CFA charter levels',
  ],
  'cfa-all-levels-lifetime-package-349': [
    'CFA all levels package',
    'CFA Level 1 2 3 mock exam lifetime',
    'CFA mock exam lifetime access',
    'buy CFA L1 L2 L3 mocks together',
  ],
};

/**
 * SEO blog — ordered by search priority (highest intent first).
 * Titles and meta descriptions match common CFA Level 1 queries.
 */
export const BLOG_POSTS = [
  {
    slug: 'free-cfa-level-1-mock-exam-2026',
    title: 'Free CFA Mock Exam: Timed Level 1 Mock 1',
    metaDescription:
      'Take a free CFA Level 1 mock exam: one timed 90-question mock test in 135 minutes, with CBT tools and an instant score. No account or credit card needed.',
    published: '2026-09-10',
    updated: '2026-10-27',
    ctaInHero: true,
    sections: [
      {
        heading: 'Start Mock 1 in minutes',
        paragraphs: [
          'This is the free CFA® Level 1 mock on CFA Mock Exam: one complimentary 90-question sitting (Mock 1), timed like a Prometric session block, with CBT-style flagging, a question navigator, and an on-screen BA II Plus. No credit card.',
          'Open Mock 1 — no account needed — and work under the clock. You get an instant score when you finish. Topic breakdown, explanations, ranked analysis, and Mocks 2–8 unlock with lifetime or monthly access — not with this complimentary sitting.',
        ],
        bullets: [
          '90 multiple-choice items · 135 minutes · official 2026 topic weights',
          'CBT flag, review list, and question navigator',
          'On-screen TI BA II Plus during the sitting',
          'Instant score free — no payment to start',
        ],
      },
      {
        heading: 'What “free” means here',
        paragraphs: [
          'One complimentary Mock 1 per signed-in account. It is a full session-length mock, not a 10-question teaser and not a drip of isolated practice items.',
          'After you submit, your score shows where you stand against the common 70% mock target. That baseline is the point of Mock 1: know your level before you buy more sittings or burn another study week. Paid access adds the topic breakdown (Ethics, FSA, Fixed Income, and the rest) so you can see exactly where you leaked points.',
        ],
      },
      {
        heading: 'Practice exam or question bank: which should you use?',
        paragraphs: [
          'Use a timed practice exam to rehearse pacing across topics. Use a question bank or topic drill to work on one weak area, then return to a fresh timed mock. Repeating a remembered question is less useful as a readiness check than solving a new one.',
          'CFA Institute provides registered candidates with practice questions and mock exams in its [Learning Ecosystem](https://www.cfainstitute.org/programs/cfa-program/candidate-resources/mock-exam-and-practice-questions). Its optional Practice Pack adds more official practice. This site offers independent supplementary mocks; our questions are not official CFA Institute exam questions.',
          'For worked examples without a timer, use the [free Level 1 sample questions](/blog/cfa-level-1-practice-questions-free). For a timed baseline, start Mock 1 above; no account is needed.',
        ],
      },
      {
        heading: 'Plan your practice around your exam window',
        paragraphs: [
          'Leave time to review mistakes between timed sittings. Our [mock practice guide](/blog/cfa-level-1-mock-exam-how-to-practice) explains pacing and review, and the [Level 1 study plan](/blog/how-to-pass-cfa-level-1-study-plan) helps place mocks in your preparation. For when to start and how to keep an error log, read the [CFA practice exam guide](/cfa-practice-exam/).',
          'Still choosing a sitting? See [how CFA Level 1 exam fees work](/cfa-level-1-exam-fees/) and the [February 2027 deadlines](/blog/cfa-level-1-february-2027-deadlines-fees). Exam registration is paid separately to CFA Institute; taking this practice exam does not register you for the real exam.',
        ],
      },
      {
        heading: 'After Mock 1 — Mocks 2–8',
        paragraphs: [
          'Lifetime access unlocks seven more weighted 90-question forms (no shared stems across the eight mocks), every explanation, post-mock analysis, and topic drills from a 720+ item bank.',
          'Lifetime access is a one-time payment that never renews — $99 for Level 1. Prefer lower commitment? Level 1 is also $19/mo, cancel anytime. Check the pricing page for the live rate.',
        ],
      },
      {
        heading: 'How this mock compares to exam day',
        paragraphs: [
          'The live Level 1 exam is 180 items in two 135-minute sessions. Mock 1 mirrors one of those session blocks: 90 questions, three choices (A, B, C), about 90 seconds each. Sit two paid mocks back-to-back later if you want a full 180-item day.',
          'This independent practice site is not affiliated with CFA Institute. CFA® and Chartered Financial Analyst® are trademarks of CFA Institute.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'Is the CFA Level 1 mock exam really free?',
            a: 'Yes. One complimentary 90-question Mock 1 per signed-in account — no credit card. Your score is included; topic breakdown, explanations, and Mocks 2–8 need lifetime or monthly access.',
          },
          {
            q: 'Do I need to pay before I start?',
            a: 'No. Start Mock 1 from the button on this page — no account needed. Payment (lifetime or monthly) is only when you want the remaining mocks and explanations.',
          },
          {
            q: 'How long is Mock 1?',
            a: '90 questions in 135 minutes — one official Level 1 session block. The live exam is two such sessions on one day.',
          },
          {
            q: 'Is this an official CFA Institute mock test?',
            a: 'No. Mock 1 is an independent practice mock exam with original questions; this site is not affiliated with CFA Institute. Registered candidates also get official practice questions and mock exams from CFA Institute.',
          },
          {
            q: 'Is this the same as the practice-questions guide?',
            a: 'No. This page is the sitting: start Mock 1 now. The practice-questions article covers how to use a larger Q-bank and review loop after you have a baseline score.',
          },
        ],
      },
    ],
    cta: { label: 'Start Mock 1 — 90 questions, no credit card', href: '/quiz/session-1' },
    pricingCta: ANNUAL_PRICING_CTA,
  },
  {
    slug: 'cfa-level-2-mock-exam-2026',
    title: 'CFA Level 2 Mock Exam 2026 | Timed Item-Set Practice',
    metaDescription:
      'CFA Level 2 mock exam: one free Mock 1, then four timed item-set forms (92Q), 2026 topic weights. Level 2 lifetime access or $29/mo on CFA Mock Exam.',
    published: '2026-09-11',
    updated: '2026-09-11',
    ctaInHero: true,
    keywords: [
      'CFA Level 2 mock exam',
      'CFA Level 2 mock exam 2026',
      'CFA L2 item set practice',
      'timed CFA Level 2 mock',
    ],
    sections: [
      {
        heading: 'Level 2 mocks built for item-set pacing',
        paragraphs: [
          'CFA® Level 2 is not another stack of standalone multiple-choice stems. You read a vignette, then answer a linked set of questions that pull from the same case facts. Our Level 2 lifetime access unlocks four full timed mock forms — 23 item sets / 92 questions each — weighted to the 2026 curriculum bands across all ten topics.',
          'Each sitting mirrors a curriculum-style CBT block: flagging, navigator, and an instant post-mock score. Topic breakdown, explanations, and analysis are included with paid access. This independent practice site is not affiliated with CFA Institute.',
        ],
        bullets: [
          'Four original Level 2 mock forms (Mock 1–4)',
          '23 item sets · 92 questions per form · all ten L2 topics',
          'Timed CBT tools + instant score after submit',
          `Level 2 lifetime from $${L2_PRICE} (list $199) — or $29/mo`,
        ],
      },
      {
        heading: 'Why Level 2 needs dedicated mocks',
        paragraphs: [
          'Level 1 stamina helps, but Level 2 fails candidates who under-train vignette reading speed and cross-item consistency. A timed item-set mock forces you to extract tables, footnotes, and constraints once — then reuse them under the clock.',
          'Use Mock 1 as a diagnostic, review every miss by topic weight, then rotate Mocks 2–4 before your window. Pair with our guides on [item sets](/blog/cfa-level-2-item-sets-explained) and [2026 topic weights](/blog/cfa-level-2-topic-weights-2026).',
        ],
      },
      {
        heading: 'What you unlock with Level 2 access',
        paragraphs: [
          `Level 2 lifetime access is a one-time payment on this site (list $199; live price: $${L2_PRICE}), or $29/mo if you prefer to cancel anytime. Check [/level/2](/level/2) for the current rate.`,
          'One complimentary Level 2 Mock 1 is included (separate from Level 1). Sign in, sit `/quiz/l2/session-1` free once, then choose Level 2 lifetime or monthly access for Mocks 2–4 and explanations.',
        ],
      },
      {
        heading: 'Level 1 vs Level 2 practice on this site',
        paragraphs: [
          'Still on Level 1? Start with the [free Level 1 Mock 1](/blog/free-cfa-level-1-mock-exam-2026). Already passed L1? Read [Level 1 vs Level 2 differences](/blog/cfa-level-1-vs-level-2-difference), then try free Level 2 Mock 1 here.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'Is there a free CFA Level 2 mock exam?',
            a: 'Yes — one complimentary Level 2 Mock 1 per account at /quiz/l2/session-1. Mocks 2–4 and explanations unlock with Level 2 lifetime access.',
          },
          {
            q: 'How many questions are in each Level 2 mock?',
            a: 'Each form has 23 item sets and 92 multiple-choice questions spanning all ten Level 2 topics at 2026 weight bands.',
          },
          {
            q: 'Does Apple Pay / card checkout unlock Level 2 immediately?',
            a: 'Yes. After a successful Stripe payment, webhook and claim-pass stamp Level 2 entitlements on your account. Open /quiz/l2 while signed in with the purchase email.',
          },
          {
            q: 'Is this affiliated with CFA Institute?',
            a: 'No. CFA® and Chartered Financial Analyst® are trademarks of CFA Institute. Confirm official dates, fees, and curriculum on CFA Institute’s site.',
          },
        ],
      },
    ],
    cta: { label: `Start free L2 Mock 1`, href: '/quiz/l2/session-1' },
    pricingCta: L2_PRICING_CTA,
  },
  {
    slug: 'cfa-level-3-mock-exam-2026',
    title: 'CFA Level 3 Mock Exam 2026 | Mixed Item Sets + Essays',
    metaDescription:
      'CFA Level 3 mock exam: one free Mock 1, then four timed 132-minute mixed sessions (item sets + constructed response). Level 3 lifetime access or $29/mo.',
    published: '2026-09-11',
    updated: '2026-09-12',
    level: 'l3',
    ctaInHero: true,
    keywords: [
      'CFA Level 3 mock exam',
      'CFA Level 3 mock exam 2026',
      'CFA L3 constructed response practice',
      'timed CFA Level 3 mock',
    ],
    sections: [
      {
        heading: 'Level 3 mocks built for the mixed CBT format',
        paragraphs: [
          'CFA® Level 3 mixes vignette item sets (four MCQs) with constructed-response essay sets in each 132-minute session. Our Level 3 lifetime access unlocks four original practice sessions — alternating 5+6 and 6+5 mixes — covering 2026 core topics and specialized pathways (Portfolio Management, Private Markets, Private Wealth).',
          'Each sitting includes flagging, navigator, on-screen BA II Plus, auto-scored MCQs, and model answers for essays. This independent practice site is not affiliated with CFA Institute — confirm official format and outlines on their site.',
        ],
        bullets: [
          'Four original Level 3 timed forms (Mock 1–4)',
          '132-minute sessions · mixed item sets + constructed response',
          '2026 core weights + pathway coverage',
          `Level 3 lifetime from $${L3_PRICE} (list $199) — or $29/mo`,
        ],
      },
      {
        heading: 'Why Level 3 needs dedicated practice',
        paragraphs: [
          'Level 2 item-set stamina helps, but Level 3 fails candidates who never write under a timer or skip command-word discipline. A mixed session forces you to switch between MCQ pacing and concise constructed responses.',
          'Use Mock 1 as a diagnostic, review model answers for essays, then rotate Mocks 2–4. Pair with our [Level 3 exam format](/blog/cfa-level-3-exam-format-2026) guide.',
        ],
      },
      {
        heading: 'What you unlock with Level 3 access',
        paragraphs: [
          `Level 3 lifetime access is a one-time payment on this site (same list as Level 2, $199; live price: $${L3_PRICE}), or $29/mo if you prefer to cancel anytime. Check [/level/3](/level/3) for the current rate.`,
          'One complimentary Level 3 Mock 1 is included (separate from L1/L2). Sign in, sit `/quiz/l3/session-1` free once, then choose Level 3 lifetime or monthly access for Mocks 2–4. Already own Level 1 or Level 2? Upgrade to the $349 L1+L2+L3 lifetime package on [/billing](/billing) with credit for prior purchases.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'Is there a free CFA Level 3 mock exam?',
            a: 'Yes — one complimentary Level 3 Mock 1 per account at /quiz/l3/session-1. Mocks 2–4 unlock with Level 3 lifetime access.',
          },
          {
            q: 'Does each mock match a full Institute exam?',
            a: 'Each of our forms is one 132-minute practice session (one Institute CBT block). The official exam is two such sessions (22 sets total).',
          },
          {
            q: 'Are constructed responses auto-scored?',
            a: 'No. MCQs are auto-scored; essays show a model answer on review so you can compare command-word responses.',
          },
          {
            q: 'Is this affiliated with CFA Institute?',
            a: 'No. CFA® and Chartered Financial Analyst® are trademarks of CFA Institute. Confirm official dates, fees, and curriculum on CFA Institute’s site.',
          },
        ],
      },
    ],
    cta: { label: `Start free L3 Mock 1`, href: '/quiz/l3/session-1' },
    pricingCta: L3_PRICING_CTA,
  },
  {
    slug: 'cfa-all-levels-lifetime-package-349',
    title: 'CFA L1+L2+L3 Lifetime Package — $349 (List $499)',
    metaDescription:
      'One payment unlocks CFA Level 1, 2 and 3 mock exams for life: $349 (list $499). Prior level purchases credit on /billing, and free Mock 1 per level stays.',
    published: '2026-09-14',
    updated: '2026-09-14',
    ctaInHero: true,
    keywords: [
      'CFA all levels package',
      'CFA Level 1 2 3 mock exam lifetime',
      'CFA mock exam lifetime access',
      'buy CFA L1 L2 L3 mocks together',
    ],
    sections: [
      {
        heading: 'One payment. All three levels. Lifetime.',
        paragraphs: [
          'If you plan to sit more than one CFA® level — or you want every mock bank we ship without rebuying each year — the all-level lifetime package is the clean path: $349 (list $499) for Level 1 + Level 2 + Level 3 access on this site, with product updates included.',
          'This is independent practice software, not affiliated with CFA Institute. Confirm official fees and windows on their site.',
        ],
        bullets: [
          'L1: eight timed 90-question mocks + analysis (one free Mock 1)',
          'L2: four timed item-set mocks (one free Mock 1)',
          'L3: four timed mixed sessions — item sets + constructed response (one free Mock 1)',
          'Pay once — lifetime entitlement, not a subscription',
        ],
      },
      {
        heading: 'Annual vs lifetime — when $349 wins',
        paragraphs: [
          `A single-level lifetime purchase is still the right buy if you only need one level this window (L1 $${currentPassPriceUsd()}, list $99; L2/L3 $${L2_PRICE}, list $199). The all-level package wins when you expect two or more levels, or already own one level and want to upgrade.`,
          'Already paid for L1, L2, or L3 on this account? Checkout credits what you paid toward $349 on [/billing](/billing) and [/pricing](/pricing) — you only pay the difference.',
        ],
      },
      {
        heading: 'How to buy',
        paragraphs: [
          'Open [/pricing](/pricing), find All-level lifetime package, and check out with Stripe (one-time payment). Sign in first so access attaches to your account. Prefer level hubs? [/level/2](/level/2) and [/level/3](/level/3) sell single-level access; upgrade to the package anytime from billing after a level purchase.',
          'Still deciding? Sit complimentary Mock 1 on each level free — [L1](/quiz/session-1), [L2](/quiz/l2/session-1), [L3](/quiz/l3/session-1) — then pick a single level vs the package.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'Is $349 a subscription?',
            a: 'No. One Stripe payment. Lifetime access to L1+L2+L3 mocks on this site. No auto-renew.',
          },
          {
            q: 'Do free Mock 1 sits still work?',
            a: 'Yes. One complimentary Mock 1 per level (L1, L2, L3) remains available without the package. The package unlocks Mocks 2–8 and ongoing access on all three.',
          },
          {
            q: 'Can I upgrade after buying Level 1?',
            a: 'Yes. What you already paid for any level credits toward $349. See /billing for the quoted difference.',
          },
          {
            q: 'Is this affiliated with CFA Institute?',
            a: 'No. CFA® and Chartered Financial Analyst® are trademarks of CFA Institute. Confirm official dates, fees, and curriculum on CFA Institute’s site.',
          },
        ],
      },
    ],
    cta: PACKAGE_PRICING_CTA,
    pricingCta: PACKAGE_PRICING_CTA,
  },
  {
    slug: 'cfa-level-3-exam-dates-2026',
    title: 'CFA Level 3 Exam Dates 2026–2027 (Next: Feb 18–21)',
    metaDescription:
      'CFA Level 3 exam windows: next is 18–21 February 2027 after August 2026 closed. L3 sits Feb/Aug only. Confirm officially, then practice mixed mocks.',
    published: '2026-09-12',
    updated: '2026-09-12',
    level: 'l3',
    keywords: [
      'CFA Level 3 exam dates 2026',
      'CFA Level 3 February 2027',
      'CFA L3 exam windows',
      'Level 3 exam schedule',
    ],
    sections: [
      {
        heading: 'Next Level 3 window',
        paragraphs: [
          'As of mid-September 2026, the next CFA® Level 3 window is 18–21 February 2027. The August 2026 sitting (13–17 Aug) has closed. Level 3 does not offer May or November windows.',
          'Confirm registration and scheduling on CFA Institute’s [dates & fees](https://www.cfainstitute.org/programs/cfa-program/dates-fees) page. This site is independent practice only.',
        ],
        bullets: [
          'Next: February 2027 — 18–21 Feb',
          'Past: February 2026 — 29 Jan–1 Feb',
          'Past: August 2026 — 13–17 Aug',
        ],
      },
      {
        heading: '2027 Level 3 calendar',
        paragraphs: [
          'Level 3 returns twice in 2027: February and August. Missing February means waiting until August.',
        ],
        bullets: [
          'February 2027 — 18–21 Feb',
          'August 2027 — 11–14 Aug',
        ],
      },
      {
        heading: 'How to prep here',
        paragraphs: [
          'Unlock four mixed 132-minute sessions on [/level/3](/level/3). Read the [exam format](/blog/cfa-level-3-exam-format-2026) and [constructed response](/blog/cfa-level-3-constructed-response-guide) guides. Still finishing Level 2? See [Level 2 November dates](/blog/cfa-level-2-exam-dates-2026).',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'When is the next CFA Level 3 exam?',
            a: '18–21 February 2027 is the next published Level 3 window after August 2026. Confirm on CFA Institute’s dates & fees page.',
          },
          {
            q: 'Can I sit Level 3 in November?',
            a: 'No. Level 3 runs in February and August only.',
          },
          {
            q: 'Is this site affiliated with CFA Institute?',
            a: 'No. CFA® and Chartered Financial Analyst® are trademarks of CFA Institute. We provide independent mock practice.',
          },
        ],
      },
    ],
    cta: L3_PRICING_CTA,
  },
  {
    slug: 'cfa-level-3-exam-format-2026',
    title: 'CFA Level 3 Exam Format 2026 (Mixed Sessions Explained)',
    metaDescription:
      'CFA Level 3 exam format 2026: two 132-minute mixed sessions, 11 item sets + 11 essay sets, pathways, and how to practice constructed response.',
    published: '2026-09-11',
    updated: '2026-09-12',
    level: 'l3',
    keywords: [
      'CFA Level 3 exam format',
      'CFA Level 3 constructed response',
      'CFA Level 3 item sets',
      'CFA Level 3 pathways 2026',
    ],
    sections: [
      {
        heading: 'Mixed format in both sessions',
        paragraphs: [
          'For recent windows, CFA Institute delivers Level III as two equal 132-minute CBT sessions. Item sets and constructed-response (essay) sets are mixed in each session — typically 6+5 or 5+6 — rather than essays-only in the morning.',
          'Overall the exam includes 11 item sets and 11 essay sets at 12 points each. Item sets use a vignette plus four multiple-choice items (3 points each). Essay sets use bold command words and typed responses.',
        ],
      },
      {
        heading: '2026 curriculum shape',
        paragraphs: [
          'Core topics include Asset Allocation, Portfolio Construction, Performance Measurement, Derivatives and Risk Management, and Ethics, plus a large specialized pathway weight (Portfolio Management, Private Markets, or Private Wealth). Always verify the current outline on CFA Institute’s site before you sit.',
        ],
      },
      {
        heading: 'How to practice here',
        paragraphs: [
          'Our [Level 3 mocks](/blog/cfa-level-3-mock-exam-2026) are one-session timed sittings with the same mixed structure. Train MCQ pacing and written command-word answers in the same block — that is the skill the new format rewards.',
        ],
      },
    ],
    cta: { label: `Unlock Level 3 mocks — $${L3_PRICE} lifetime`, href: '/level/3' },
    pricingCta: L3_PRICING_CTA,
  },
  {
    slug: 'cfa-level-3-constructed-response-guide',
    title: 'CFA Level 3 Constructed Response Guide (Command Words)',
    metaDescription:
      'CFA Level 3 constructed response guide: command words, scoring discipline, timing, and how to practice essays inside mixed 132-minute mocks.',
    published: '2026-09-12',
    updated: '2026-09-12',
    level: 'l3',
    keywords: [
      'CFA Level 3 constructed response',
      'CFA Level 3 essay command words',
      'CFA L3 written response tips',
      'how to answer CFA Level 3 essays',
    ],
    sections: [
      {
        heading: 'Essays are graded on the command word',
        paragraphs: [
          'Constructed-response sets on CFA® Level 3 use bold command words (Calculate, Determine, Justify, Recommend, Contrast, and similar). Graders award points for answering the exact ask — not for dumping every related fact you remember.',
          'Before you type, underline the command word and the subject. A perfect portfolio theory paragraph that never “justifies” the recommendation scores poorly.',
        ],
        bullets: [
          'Read the command word first, then the vignette facts you need',
          'Lead with the answer, then supporting points — not the reverse',
          'Use labels (i), (ii) when the prompt asks for two reasons',
          'Leave blank lines between parts so graders can find points quickly',
        ],
      },
      {
        heading: 'Timing inside a mixed session',
        paragraphs: [
          'Institute sessions mix item sets and essay sets in the same 132 minutes. Do not “save all essays for the end.” Treat each 12-point essay set like a timed case: rough target ~12 minutes, then move on.',
          'If an essay stalls, write the direct answer line and one justification bullet, flag it, and return after the next item set.',
        ],
      },
      {
        heading: 'How to practice here',
        paragraphs: [
          'Our [Level 3 mocks](/blog/cfa-level-3-mock-exam-2026) interleave MCQ item sets with constructed-response sets so you practice switching modes under a timer. MCQs auto-score; essays show a model answer plus scoring notes on review so you can self-mark Full / Partial / Miss.',
          'Pair this guide with the [Level 3 exam format](/blog/cfa-level-3-exam-format-2026) overview and a [topic-weight plan](/blog/cfa-level-3-topic-weights-2026).',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'Are Level 3 essays auto-graded on this site?',
            a: 'No. Multiple-choice items are auto-scored. Constructed responses show a model answer and scoring notes so you can compare your write-up.',
          },
          {
            q: 'What command words show up most?',
            a: 'Calculate, Determine, Justify, Recommend, Explain, Contrast, and Identify appear often. Always confirm the live curriculum examples on CFA Institute’s site.',
          },
          {
            q: 'Should I write long essays?',
            a: 'No. Concise, labeled points that hit the command word beat long narrative. Graders scan for scoring elements.',
          },
        ],
      },
    ],
    cta: { label: `Unlock Level 3 mocks — $${L3_PRICE} lifetime`, href: '/level/3' },
    pricingCta: L3_PRICING_CTA,
  },
  {
    slug: 'cfa-level-3-topic-weights-2026',
    title: 'CFA Level 3 Topic Weights 2026 (Core + Pathways)',
    metaDescription:
      'CFA Level 3 topic weights 2026: core bands for Asset Allocation, Portfolio Construction, Performance, Derivatives, Ethics, plus 30–35% pathway weight.',
    published: '2026-09-12',
    updated: '2026-09-12',
    level: 'l3',
    keywords: [
      'CFA Level 3 topic weights 2026',
      'CFA Level 3 pathways',
      'Level 3 curriculum weights',
      'CFA L3 asset allocation weight',
    ],
    sections: [
      {
        heading: 'Core plus a large specialized pathway',
        paragraphs: [
          'CFA® Level 3 (2026 outline) concentrates weight in Asset Allocation and Portfolio Construction, with meaningful bands for Performance Measurement, Derivatives and Risk Management, and Ethics — plus a large specialized pathway (Portfolio Management, Private Markets, or Private Wealth) typically around 30–35%.',
          'Always verify the live weight table on CFA Institute’s curriculum pages for your exam year. Practice-site summaries are planning aids, not official policy.',
        ],
      },
      {
        heading: 'A practical priority order',
        paragraphs: [
          'After a diagnostic mixed mock, sort leaks by weight × miss rate. Pathway questions are expensive — do not treat them as “optional extras.”',
        ],
        bullets: [
          'Keep Ethics warm weekly — soft floor for borderline scores',
          'Drill Asset Allocation and Portfolio Construction case writing early',
          'Practice pathway vignettes in the same weeks as core mocks',
          'Use Performance and Derivatives for calculation speed under time',
        ],
      },
      {
        heading: 'Tie weights to timed forms',
        paragraphs: [
          'Our Level 3 mocks cover core topics and pathways inside one 132-minute mixed session so you stress the real mix — not siloed essay-only drills. See the [Level 3 mock exam](/blog/cfa-level-3-mock-exam-2026) page for access details.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'Where do official Level 3 weights come from?',
            a: 'CFA Institute publishes topic weight ranges for each level and curriculum year. Use those ranges; treat third-party summaries as secondary.',
          },
          {
            q: 'Do I need to study all three pathways?',
            a: 'You select one specialized pathway for the exam. Practice the pathway you registered for, while still covering core topics thoroughly.',
          },
          {
            q: 'How do mocks help with weights?',
            a: 'Topic bars on auto-graded items plus essay self-scores show expensive leaks. Fix high-weight misses first, then re-sit another form.',
          },
        ],
      },
    ],
    cta: L3_PRICING_CTA,
  },
  {
    slug: 'how-to-pass-cfa-level-3-study-plan',
    title: 'How to Pass CFA Level 3: Study Plan for Mixed Sessions',
    metaDescription:
      'How to pass CFA Level 3: a study plan for mixed item sets + constructed response, pathway coverage, and timed 132-minute mocks.',
    published: '2026-09-12',
    updated: '2026-09-12',
    level: 'l3',
    keywords: [
      'how to pass CFA Level 3',
      'CFA Level 3 study plan',
      'CFA L3 study schedule',
      'CFA Level 3 essay practice plan',
    ],
    sections: [
      {
        heading: 'Hours help; mixed-session reps decide',
        paragraphs: [
          'Level 3 fails candidates who only reread notes or drill MCQs. Build a weekly loop: pathway + core reading → short constructed-response drills → one timed mixed block → error log on command words and calculation misses.',
          'Protect sleep in the final month. Switching between vignette MCQs and typed essays under fatigue is where scores collapse.',
        ],
      },
      {
        heading: 'A simple weekly template',
        paragraphs: [
          'Aim for two shorter mixed practice blocks mid-week and one full 132-minute form on the weekend once your bank unlocks. Between mocks, rewrite weak essay answers against model responses — do not just re-read them.',
        ],
        bullets: [
          'Mon–Tue: pathway + one core topic with command-word drills',
          'Wed: timed half-session (mix of item sets and essays)',
          'Thu–Fri: repair Ethics / AA / Construction leaks',
          'Sat or Sun: full Mock form + self-score essays',
        ],
      },
      {
        heading: 'Practice tools on this site',
        paragraphs: [
          'Unlock four Level 3 forms at [/level/3](/level/3), then rotate Mock 1–4. Review MCQ explanations and essay model answers after each sitting. For format context, read the [exam format guide](/blog/cfa-level-3-exam-format-2026) and [constructed response tips](/blog/cfa-level-3-constructed-response-guide).',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'Is there a free Level 3 mock?',
            a: 'Yes — Level 3 Mock 1 is free (score only). Level 3 lifetime or monthly access unlocks the other three mixed sessions.',
          },
          {
            q: 'How many full mocks should I sit?',
            a: 'Most candidates improve after 3–4 timed mixed sessions with honest essay self-scoring between them. Quality of review beats volume of unread mocks.',
          },
          {
            q: 'Should I still practice Level 2 item sets?',
            a: 'Vignette reading speed transfers, but Level 3 needs dedicated essay reps. Prefer L3 mixed forms once you are inside eight weeks of the exam.',
          },
        ],
      },
    ],
    cta: L3_PRICING_CTA,
  },
  {
    slug: 'cfa-level-2-vs-level-3-difference',
    title: 'CFA Level 2 vs Level 3: Format, Skills, and Prep Shift',
    metaDescription:
      'CFA Level 2 vs Level 3: item-set vignettes versus mixed constructed response, pathway weight, and how to change your practice mix.',
    published: '2026-09-12',
    updated: '2026-09-12',
    level: 'l3',
    keywords: [
      'CFA Level 2 vs Level 3',
      'CFA Level 3 format difference',
      'item sets vs constructed response',
      'CFA charter levels',
    ],
    sections: [
      {
        heading: 'Different exam muscles',
        paragraphs: [
          'Level 2 is built around item-set vignettes (case + multiple MCQs). Level 3 keeps vignettes but adds constructed-response sets with command words inside the same timed session. Candidates who only train L2-style MCQs underperform on essay grading.',
          'Level 3 also introduces a large specialized pathway weight. Your prep calendar must include pathway cases, not only “core PM” leftovers from Level 2.',
        ],
      },
      {
        heading: 'How practice should change',
        paragraphs: [
          'After you pass Level 2, shift weekly reps toward mixed sessions: item-set pacing plus typed answers. Keep Ethics warm. Use [Level 2 mocks](/blog/cfa-level-2-mock-exam-2026) only if vignette reading speed is still weak; otherwise move to [Level 3 mocks](/blog/cfa-level-3-mock-exam-2026).',
        ],
        bullets: [
          'L2 skill that transfers: case reading and BA II Plus speed',
          'L3 skill that is new: command-word essays under the same timer',
          'Shared risk: Ethics soft floor on both levels',
        ],
      },
      {
        heading: 'What we offer for each level',
        paragraphs: [
          'This site sells separate lifetime or monthly access per level (and an all-level lifetime package). Each level has its own free Mock 1. See [/level/2](/level/2) and [/level/3](/level/3) for live prices.',
        ],
      },
    ],
    cta: L3_PRICING_CTA,
  },
  {
    slug: 'cfa-level-2-item-sets-explained',
    title: 'CFA Level 2 Item Sets Explained (Vignette Strategy)',
    metaDescription:
      'CFA Level 2 item sets explained: how vignettes work, pacing per case, common traps, and how to practice timed item-set mocks for 2026.',
    published: '2026-09-11',
    updated: '2026-09-11',
    keywords: [
      'CFA Level 2 item sets',
      'CFA vignette questions',
      'how Level 2 item sets work',
      'CFA L2 case questions',
    ],
    sections: [
      {
        heading: 'What an item set is',
        paragraphs: [
          'A Level 2 item set is a short case (the vignette) followed by several multiple-choice questions that share the same facts, exhibits, and assumptions. You are graded on reading once, tracking constraints, and answering consistently — not on isolated recall.',
          'Compared with Level 1 standalone stems, wrong answers often reuse a number from the wrong exhibit or ignore a footnote that flips the conclusion.',
        ],
      },
      {
        heading: 'Pacing that survives exam day',
        paragraphs: [
          'Budget reading time up front. Skim the questions first for what the case must answer, then read the vignette with a pencil trail for rates, dates, and “except” language. Do not re-read the full case for every item unless a later question introduces a new assumption.',
          'If one item is a sinkhole, flag it and protect the rest of the set — a single hard valuation digression can steal minutes from three easier application items.',
        ],
        bullets: [
          'Preview the question stems before deep reading',
          'Mark exhibits you will need (income statement, duration table, IPS constraints)',
          'Answer easy fact/application items before multi-step calculations',
          'Keep a running list of case assumptions that later items may change',
        ],
      },
      {
        heading: 'Traps unique to vignettes',
        paragraphs: [
          'Distractors often calculate the right formula on the wrong base year, mix beginning vs ending inventory, or apply a Level 1 rule that Level 2 overrides in the case. Ethics item sets still punish selective reading of standards language.',
          'Train this with full timed mocks — not only topic quizzes — so your brain practices case reuse under fatigue. Start with our [Level 2 mock exam](/blog/cfa-level-2-mock-exam-2026) overview.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'How many questions are in a Level 2 item set?',
            a: 'Official exams use multi-question vignettes; practice banks vary. On this site each mock form uses 23 item sets totaling 92 questions.',
          },
          {
            q: 'Should I read the vignette or the questions first?',
            a: 'Most candidates benefit from a 20–30 second stem skim, then a focused vignette read. Pick one method and keep it consistent across mocks.',
          },
          {
            q: 'Do Level 1 mocks prepare me for item sets?',
            a: 'They build stamina and topic vocabulary, but not case reuse. Add dedicated Level 2 item-set timed practice before your L2 window.',
          },
        ],
      },
    ],
    cta: L2_PRICING_CTA,
  },
  {
    slug: 'cfa-level-2-topic-weights-2026',
    title: 'CFA Level 2 Topic Weights 2026 (Study Priority Map)',
    metaDescription:
      'CFA Level 2 topic weights 2026: how to prioritize FRA, Equity, Fixed Income, Derivatives, and Ethics. Build a mock plan around the official bands.',
    published: '2026-09-11',
    updated: '2026-09-11',
    keywords: [
      'CFA Level 2 topic weights 2026',
      'CFA L2 curriculum weights',
      'Level 2 exam topic breakdown',
      'CFA Level 2 FRA weight',
    ],
    sections: [
      {
        heading: 'Use weights to decide what to mock first',
        paragraphs: [
          'Level 2 topic weights are published as ranges by CFA Institute. Plan study hours toward the top of high-weight bands (Financial Reporting, Equity, Fixed Income, Derivatives, Portfolio Management, Ethics) and keep lower-weight topics alive with spaced drills — not zero practice.',
          'Always confirm the live weight table on CFA Institute’s curriculum pages for your exam year. Weights on practice sites are planning aids, not official policy.',
        ],
      },
      {
        heading: 'A practical priority order',
        paragraphs: [
          'After a diagnostic mock, sort misses by weight × frequency. A medium miss rate in a high-weight topic beats a high miss rate in a tiny topic for score recovery.',
        ],
        bullets: [
          'Stabilize Ethics vignettes weekly — soft floor for borderline scores',
          'Drill FRA and Equity item sets until case reading feels automatic',
          'Keep Fixed Income and Derivatives calculation speed sharp',
          'Do not orphan Alternatives / PM — they still appear in mixed mocks',
        ],
      },
      {
        heading: 'Tie weights to timed forms',
        paragraphs: [
          'Our Level 2 mocks are built across all ten topics inside 2026 weight bands so one sitting stresses the mix you will see on exam day — not a single-topic silo. See the [Level 2 mock exam](/blog/cfa-level-2-mock-exam-2026) page for access details.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'Where do official CFA Level 2 topic weights come from?',
            a: 'CFA Institute publishes topic weight ranges for each level and curriculum year. Use those ranges; treat third-party summaries as secondary.',
          },
          {
            q: 'Should I study only the heaviest topics?',
            a: 'No. Over-concentrating creates brittle scores when a lighter topic vignette lands. Maintain coverage, then overweight weak high-band topics.',
          },
          {
            q: 'How do mocks help with weights?',
            a: 'Topic bars after a full form show where you leaked points relative to the mix. Fix the expensive leaks first, then re-sit another form.',
          },
        ],
      },
    ],
    cta: L2_PRICING_CTA,
  },
  {
    slug: 'how-to-pass-cfa-level-2-study-plan',
    title: 'How to Pass CFA Level 2: Study Plan That Fits Item Sets',
    metaDescription:
      'How to pass CFA Level 2: a practical study plan for item-set vignettes, timed mocks and weak-topic repair. Benchmark first with free Level 2 Mock Exam 1.',
    published: '2026-09-11',
    updated: '2026-09-11',
    keywords: [
      'how to pass CFA Level 2',
      'CFA Level 2 study plan',
      'CFA L2 study schedule',
      '300 hours CFA Level 2',
    ],
    sections: [
      {
        heading: 'Hours are necessary; item-set reps are decisive',
        paragraphs: [
          'Most candidates cite ~300+ hours for Level 2, but hours spent only on notes without timed vignettes underperform. Build a weekly loop: learn → short item-set drill → weekly mixed timed block → error log.',
          'Protect sleep in the final month. Cognitive load on long cases collapses when you are exhausted.',
        ],
      },
      {
        heading: '12-week skeleton (adjust to your window)',
        paragraphs: [
          'Weeks 1–4: curriculum coverage with light item sets. Weeks 5–8: heavier mixed sets and first full mock. Weeks 9–11: two to three more full mocks with deep review. Final week: light Ethics, formula refresh, logistics.',
        ],
        bullets: [
          'One full Level 2 timed mock every 10–14 days after mid-prep',
          'Error log columns: topic, vignette trap, formula, fix action',
          'Weekly Ethics vignette set even when Ethics “feels fine”',
          'Calculator fluency for FI / derivatives nested inside cases',
        ],
      },
      {
        heading: 'Where mocks fit',
        paragraphs: [
          'Use four distinct forms if you can — repeating the same mock trains memory of stems, not skill. Our [Level 2 lifetime access](/blog/cfa-level-2-mock-exam-2026) unlocks four original forms. Pair with the [item-set strategy guide](/blog/cfa-level-2-item-sets-explained).',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'Can I pass Level 2 studying like Level 1?',
            a: 'Not reliably. Level 2 rewards case integration. Keep L1 discipline on ethics and formulas, but add vignette timing practice.',
          },
          {
            q: 'How many Level 2 mocks should I sit?',
            a: 'Aim for at least three full timed forms with thorough review. Four is better if your calendar allows spacing.',
          },
          {
            q: 'When should I start Level 2 after Level 1?',
            a: 'As soon as your L1 results and Practical Skills Module requirements allow registration for an eligible window — confirm on CFA Institute’s site.',
          },
        ],
      },
    ],
    cta: L2_PRICING_CTA,
  },
  {
    slug: 'cfa-level-2-exam-format-2026',
    title: 'CFA Level 2 Exam Format 2026: Sessions, Item Sets, CBT',
    metaDescription:
      'CFA Level 2 exam format 2026: item-set structure, session timing, CBT tools, and how to practice with timed mocks before your window.',
    published: '2026-09-11',
    updated: '2026-09-11',
    keywords: [
      'CFA Level 2 exam format',
      'CFA Level 2 sessions',
      'Level 2 CBT format',
      'CFA L2 question count',
    ],
    sections: [
      {
        heading: 'Format at a glance',
        paragraphs: [
          'Level 2 is a computer-based exam delivered in sessions with vignette-based item sets (multiple questions per case). Exact question counts and timing are set by CFA Institute for each cycle — always verify on the official exam details page before you schedule.',
          'Practice should feel like CBT: flagging, review screens, and sustained reading under a clock. Paper PDF drills alone under-train navigation and eye fatigue.',
        ],
      },
      {
        heading: 'What transfers from Level 1',
        paragraphs: [
          'Approved calculator habits, Ethics careful reading, and session stamina transfer. What does not transfer is standalone stem pacing — Level 2 needs case reuse discipline. See [Level 1 vs Level 2](/blog/cfa-level-1-vs-level-2-difference).',
        ],
      },
      {
        heading: 'How we mirror practice',
        paragraphs: [
          'Our Level 2 mocks use 23 item sets / 92 questions per form with CBT-style tools and post-mock topic bars. Unlock via [Level 2 lifetime or monthly access](/level/2). Not affiliated with CFA Institute.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'Is Level 2 still multiple choice?',
            a: 'Yes — questions are multiple choice, but they are tied to vignettes (item sets) rather than mostly standalone stems.',
          },
          {
            q: 'How should I practice the CBT format?',
            a: 'Sit full timed digital mocks with flagging and review. Treat navigation as part of the skill, not an afterthought.',
          },
          {
            q: 'Where do I confirm official 2026 format details?',
            a: 'On CFA Institute’s exam and curriculum pages for Level 2. Third-party sites can summarize but are not authoritative.',
          },
        ],
      },
    ],
    cta: L2_PRICING_CTA,
  },
  {
    slug: 'cfa-level-2-exam-dates-2026',
    title: 'CFA Level 2 Exam Dates 2026–2027 (Next: Nov 18–22)',
    metaDescription:
      'CFA Level 2 exam windows: next is 18–22 November 2026, then May/Aug/Nov 2027. No February sitting. Confirm on CFA Institute, then practice timed item sets.',
    published: '2026-09-12',
    updated: '2026-09-12',
    level: 'l2',
    keywords: [
      'CFA Level 2 exam dates 2026',
      'CFA Level 2 November 2026',
      'CFA L2 exam windows',
      'Level 2 exam schedule 2027',
    ],
    sections: [
      {
        heading: 'Next Level 2 window',
        paragraphs: [
          'As of mid-September 2026, the next CFA® Level 2 window is 18–22 November 2026. May and August 2026 have already closed. Level 2 does not offer a February sitting.',
          'Register and schedule on CFA Institute’s [dates & fees](https://www.cfainstitute.org/programs/cfa-program/dates-fees) tool — this independent practice site does not sell exam seats.',
        ],
        bullets: [
          'Next: November 2026 — 18–22 Nov',
          'Past: May 2026 — 19–23 May',
          'Past: August 2026 — 25–29 Aug',
        ],
      },
      {
        heading: '2027 Level 2 calendar (published windows)',
        paragraphs: [
          'After November 2026, Level 2 returns in May, August, and November 2027. Confirm any registration deadlines on the official site before you pay.',
        ],
        bullets: [
          'May 2027 — 18–22 May',
          'August 2027 — 23–27 Aug',
          'November 2027 — 20–24 Nov',
        ],
      },
      {
        heading: 'How to prep here',
        paragraphs: [
          'Unlock four timed item-set mocks on [/level/2](/level/2). Pair with [item-set strategy](/blog/cfa-level-2-item-sets-explained) and the [Level 2 format guide](/blog/cfa-level-2-exam-format-2026). Still on Level 1? See [Level 1 November dates](/blog/cfa-level-1-exam-dates-2026). Sitting Level 3 next? See [Level 3 dates](/blog/cfa-level-3-exam-dates-2026).',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'When is the next CFA Level 2 exam?',
            a: '18–22 November 2026 is the next published Level 2 window after August 2026 closed. Always confirm on CFA Institute’s dates & fees page.',
          },
          {
            q: 'Is there a February Level 2 exam?',
            a: 'No. Level 2 runs in May, August, and November only.',
          },
          {
            q: 'Do you sell CFA Institute registration?',
            a: 'No. We sell independent mock practice. Register for the exam only through CFA Institute.',
          },
        ],
      },
    ],
    cta: L2_PRICING_CTA,
  },
  {
    slug: 'cfa-level-1-practice-questions-free',
    title: 'CFA Level 1 Practice Questions with Answers',
    metaDescription:
      'CFA Level 1 Practice Questions: 10 free sample questions with explanations, then sit a free full Mock Exam — 90 timed questions, instant score.',
    h1: 'CFA Level 1 Practice Questions: Free Samples with Answers',
    published: '2026-08-23',
    updated: '2026-11-04',
    ctaInHero: true,
    heroCta: { label: 'Try free Mock 1: 90 timed questions, instant score', href: '/quiz/session-1' },
    sections: [
      {
        heading: 'Free CFA Level 1 practice questions (with explanations)',
        paragraphs: [
          'Looking for CFA Level 1 practice questions you can work right now? Below are 10 original Level I items in exam format (three choices, A–C) — one from each curriculum topic — with full explanations. They are written for this site, not taken from past exams.',
          'When you want a timed baseline, [sit free Mock Exam 1](/quiz/session-1): 90 questions, 135 minutes, instant score, no account or card. For 12 more worked examples, see the [CFA Level 1 practice questions and practice exam guide](/cfa-practice-exam/).',
        ],
      },
      {
        heading: 'Question 1 — Ethical and Professional Standards (Standard II(A))',
        paragraphs: [
          'An analyst combines public information with nonmaterial nonpublic information about a company, concludes the stock is overvalued, and issues a sell recommendation. Under the Standards of Professional Conduct, the analyst has:',
        ],
        bullets: [
          'A. violated Standard II(A), because her conclusion is material',
          'B. not violated Standard II(A)',
          'C. violated Standard II(A), because she used nonpublic information',
        ],
        after: [
          'Answer: B. This is the mosaic theory: reaching a material conclusion from public information plus nonmaterial nonpublic information is permitted under Standard II(A).',
          'Review: [Standards I–VII guidance notes](/study-guide/ethical-and-professional-standards/guidance-for-standards-i-vii) · [Ethics study guide](/blog/cfa-level-1-ethics-study-guide)',
        ],
      },
      {
        heading: 'Question 2 — Quantitative Methods (time value of money)',
        paragraphs: [
          'An investor deposits USD 10,000 at a stated annual rate of 6%, compounded monthly. The value after 3 years is closest to:',
        ],
        bullets: ['A. USD 11,800', 'B. USD 11,967', 'C. USD 11,910'],
        after: [
          'Answer: B. The periodic rate is 0.06 / 12 = 0.5% and n = 36 months, so FV = 10,000 × 1.005^36 = USD 11,967. A uses simple interest; C uses annual compounding.',
          'Review: [time value of money notes](/study-guide/quantitative-methods/time-value-of-money-in-finance) · [Quant TVM guide](/blog/cfa-level-1-quantitative-methods-tvm)',
        ],
      },
      {
        heading: 'Question 3 — Economics (real vs nominal GDP)',
        paragraphs: [
          'An economy’s nominal GDP rises 5% while its GDP deflator rises 2%. Real GDP growth is closest to:',
        ],
        bullets: ['A. 3%', 'B. 7%', 'C. 2.5%'],
        after: [
          'Answer: A. Approximate real growth ≈ nominal growth − inflation = 5% − 2% = 3% (exact: 1.05 / 1.02 − 1 ≈ 2.94%). B adds the rates; C halves the gap.',
          'Review: [business cycles notes](/study-guide/economics/understanding-business-cycles)',
        ],
      },
      {
        heading: 'Question 4 — Financial Statement Analysis (IFRS vs US GAAP)',
        paragraphs: ['Under IFRS, interest paid may be classified in the statement of cash flows as:'],
        bullets: ['A. operating activities only', 'B. operating or financing activities', 'C. financing activities only'],
        after: [
          'Answer: B. IFRS allows interest paid in operating or financing activities; US GAAP requires operating.',
          'Review: [statement of cash flows notes](/study-guide/financial-reporting-and-analysis/analyzing-statements-of-cash-flows-i) · [FSA tips](/blog/cfa-level-1-financial-statement-analysis-tips)',
        ],
      },
      {
        heading: 'Question 5 — Corporate Issuers (WACC)',
        paragraphs: [
          'A firm’s cost of equity is 12%, its pretax cost of debt is 6%, and its marginal tax rate is 25%. The target debt weight is 40% of firm value. The firm’s WACC is closest to:',
        ],
        bullets: ['A. 8.4%', 'B. 9.0%', 'C. 9.6%'],
        after: [
          'Answer: B. WACC = 0.60 × 12% + 0.40 × 6% × (1 − 0.25) = 7.2% + 1.8% = 9.0%. A ignores the tax shield on debt; C uses the pretax cost of debt.',
          'Review: [capital structure notes](/study-guide/corporate-issuers/capital-structure)',
        ],
      },
      {
        heading: 'Question 6 — Equity Investments (market efficiency)',
        paragraphs: [
          'In a market that is semi-strong-form efficient, security prices reflect:',
        ],
        bullets: [
          'A. all past market prices and trading volumes only',
          'B. all publicly available information',
          'C. all public and private information',
        ],
        after: [
          'Answer: B. Semi-strong form includes all public information (and therefore weak-form past prices). Strong form would add private information (C); A is weak form only.',
          'Review: [market efficiency notes](/study-guide/equity-investments/market-efficiency)',
        ],
      },
      {
        heading: 'Question 7 — Fixed Income (duration)',
        paragraphs: [
          'A bond has a modified duration of 6.0. If its yield to maturity rises by 50 basis points, the approximate percentage change in its price is:',
        ],
        bullets: ['A. −3.0%', 'B. −0.3%', 'C. +3.0%'],
        after: [
          'Answer: A. %ΔP ≈ −ModDur × ΔYield = −6.0 × 0.0050 = −3.0%. Price falls when yield rises, which rules out C.',
          'Review: [duration measures notes](/study-guide/fixed-income/yield-based-bond-duration-measures-and-properties) · [Fixed income duration guide](/blog/cfa-level-1-fixed-income-duration-guide)',
        ],
      },
      {
        heading: 'Question 8 — Derivatives (put-call parity)',
        paragraphs: [
          'For European options on a non-dividend-paying stock with the same strike and expiration, put-call parity implies that the call price minus the put price equals:',
        ],
        bullets: [
          'A. the stock price minus the present value of the strike',
          'B. the present value of the strike minus the stock price',
          'C. the stock price plus the present value of the strike',
        ],
        after: [
          'Answer: A. Put-call parity: c + PV(X) = p + S₀, so c − p = S₀ − PV(X). B reverses the signs; C adds both terms.',
          'Review: [put-call parity notes](/study-guide/derivatives/option-replication-using-put-call-parity) · [Derivatives study guide](/blog/cfa-level-1-derivatives-study-guide)',
        ],
      },
      {
        heading: 'Question 9 — Alternative Investments (private equity)',
        paragraphs: [
          'Compared with investments in publicly traded equities, private equity investments typically have:',
        ],
        bullets: [
          'A. higher liquidity and shorter investment horizons',
          'B. lower liquidity and longer investment horizons',
          'C. daily exchange mark-to-market valuations',
        ],
        after: [
          'Answer: B. Private equity is typically illiquid, with multi-year lockups and longer horizons. A and C describe public equity features.',
          'Review: [private capital notes](/study-guide/alternative-investments/investments-in-private-capital-equity-and-debt) · [Alternatives guide](/blog/cfa-level-1-alternative-investments-guide)',
        ],
      },
      {
        heading: 'Question 10 — Portfolio Management (CAPM)',
        paragraphs: [
          'The risk-free rate is 3%, the expected market return is 8%, and a stock’s beta is 1.2. Using the CAPM, the stock’s expected return is:',
        ],
        bullets: ['A. 9.0%', 'B. 9.6%', 'C. 12.6%'],
        after: [
          'Answer: A. E(R) = 3% + 1.2 × (8% − 3%) = 9.0%. B multiplies the market return by beta; C adds the risk-free rate to that.',
          'Review: [CAPM and the security market line notes](/study-guide/portfolio-management-and-wealth-planning/portfolio-risk-and-return-part-ii) · [CAPM guide](/blog/cfa-level-1-portfolio-management-capm)',
        ],
      },
      {
        callout: true,
        paragraphs: [
          'Ready for exam timing? [Sit free Mock Exam 1 — 90 questions, 135 minutes](/quiz/session-1). No account or card needed — instant score when you finish.',
          `Want more volume? [8 mocks and 720+ questions: ${L1_PRICE_LINE}](/pricing).`,
        ],
      },
      {
        heading: 'How to use practice questions',
        paragraphs: [
          'Treat each item as a drill, not a scoreboard. Cover the choices, write your answer, then read the explanation — including when you got it right. Lucky guesses on Ethics, FSA, or put-call parity create false confidence that shows up under the 90-second-per-question clock.',
          'Tag every miss by topic and skill (for example “modified duration sign error” or “WACC tax shield”). Revisit those tags in the [formula glossary](/glossary?formulas=1) and [study guide](/study-guide) before the next set. After a clean untimed pass on weak topics, move to a timed mixed set — start with [free Mock Exam 1](/quiz/session-1).',
          'For a full schedule that pairs reading with question volume, see the [300-hour Level 1 study plan](/blog/how-to-pass-cfa-level-1-study-plan) and [how to practice with mock exams](/blog/cfa-level-1-mock-exam-how-to-practice).',
        ],
        bullets: [
          'Daily: one study-guide chapter plus 20–40 questions with full review',
          'Weekly: one timed mixed set when ready (Mock 1 free; Mocks 2–8 with paid access)',
          'Always: log weak topics before moving to the next module',
        ],
      },
      {
        heading: 'How these questions map to the exam',
        paragraphs: [
          'Each question targets a heavily tested Level I skill across all ten topics. Weights and session structure are in [exam format & topic weights](/blog/cfa-level-1-exam-format-topic-weights-2026).',
        ],
        bullets: [
          'Q1 Ethics: Standard II(A) and the mosaic theory',
          'Q2 Quantitative Methods: compounding frequency and future value',
          'Q3 Economics: real GDP from nominal GDP and the GDP deflator',
          'Q4 Financial Statement Analysis: IFRS vs US GAAP cash flow classification',
          'Q5 Corporate Issuers: after-tax WACC with target weights',
          'Q6 Equity: semi-strong-form market efficiency',
          'Q7 Fixed Income: modified duration and price sensitivity',
          'Q8 Derivatives: European put-call parity',
          'Q9 Alternatives: private equity liquidity and horizon',
          'Q10 Portfolio Management: CAPM expected return',
        ],
      },
      {
        heading: 'Free vs paid, plainly',
        paragraphs: [
          `Start with [${FREE_MOCK.toLowerCase()}](/quiz/session-1) at official 2026 topic weights. Upgrade only if you want more mocks and the explanations.`,
        ],
        bullets: [
          'Free: Mock 1 — 90 questions, timed (135 minutes), instant score. No account or card needed.',
          'Free Mock 1 shows your score and which answers were right or wrong. It does not include explanations or a topic breakdown.',
          `Paid (${L1_PRICE_LINE}): Mocks 2–8, topic breakdown, an explanation for every item, post-mock analysis, and topic drills — 720+ questions in total. See [pricing](/pricing).`,
          'Also free: the [study guide](/study-guide) and [formula glossary](/glossary?formulas=1).',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'Are these real CFA exam questions?',
            a: 'No. They are original questions written for this site in the Level I format. They are not taken from past CFA exams.',
          },
          {
            q: 'How many free CFA Level 1 practice questions can I do here?',
            a: 'The 10 sample questions on this page (with explanations), plus the 90 in free Mock Exam 1 (timed, instant score, no account needed).',
          },
          {
            q: 'Where are CFA Institute’s own sample questions?',
            a: `On CFA Institute’s [Level I sample questions page](${CFA_L1_SAMPLES_URL}): 10 sample questions, one per curriculum topic.`,
          },
          {
            q: 'Are these CFA Level 1 practice questions free?',
            a: 'Yes. These 10 are free to read with explanations, and Mock Exam 1 (90 timed questions, score only) is free, no account needed. Paid access unlocks Mocks 2–8, topic drills, post-mock analysis, and explanations for every item.',
          },
          {
            q: 'How many questions are in the bank?',
            a: '720+ original CFA Level 1 practice questions covering all ten topics at 2026 curriculum weights for mixed sessions.',
          },
          {
            q: 'Do explanations show why wrong answers are wrong?',
            a: 'Yes on this page for the 10 samples. On timed mocks, full explanations come with paid access — they cover the correct approach and common distractors so you learn the trap, not just the letter.',
          },
        ],
      },
    ],
    cta: TRIAL_CTA,
    pricingCta: ANNUAL_PRICING_CTA,
  },
  {
    slug: 'how-to-pass-cfa-level-1-study-plan',
    title: 'How to Pass CFA Level 1: 300-Hour Study Plan (2026)',
    metaDescription:
      'How to pass CFA Level 1: a 300-hour study plan with a 16-week schedule by exam weight, mock timing and 2026 curriculum tips. Benchmark with free Mock Exam 1.',
    published: '2026-08-23',
    updated: '2026-08-26',
    sections: [
      {
        heading: 'The 300-hour rule of thumb',
        paragraphs: [
          'CFA Institute and most prep providers cite about 300 hours per level as a planning benchmark — not a guarantee. Spread over 4–6 months that is roughly 12–18 hours per week. Quality beats raw hours: active recall and timed practice count more than re-highlighting PDFs.',
          'Working candidates often need closer to 5–6 months if weekly hours are capped at 10–12. The remaining 2026 window is [November 11–17](/blog/cfa-level-1-exam-dates-2026) — February, May, and August have closed. Confirm the appointment on CFA Institute’s official page.',
        ],
      },
      {
        heading: 'A 16-week outline that respects exam weights',
        paragraphs: [
          'Weeks 1–4: Ethics (full pass), Quant TVM and stats, Economics fundamentals. Weeks 5–8: Financial Statement Analysis deep dive — largest calculation-heavy topic. Weeks 9–11: Corporate Issuers, Equity, Fixed Income. Weeks 12–14: Derivatives, Alternatives, Portfolio Management. Weeks 15–16: Mixed mocks, formula review, ethics vignettes again.',
          'Weight study time to exam weights — Ethics 15–20%, and FSA, Equity, and Fixed Income at 11–14% each. Do not spend equal weeks on Derivatives (5–8%) and Ethics.',
        ],
        bullets: [
          'One full mock every 2–3 weeks after week 6',
          'Complete the required Practical Skills Module before results release',
          'Independent site tip: try [complimentary Mock 1](/quiz/session-1) here for a practical baseline score',
          'Turn a mock score into a week-by-week plan with the free [study plan generator](/study-plan)',
        ],
      },
      {
        heading: 'Weekly rhythm that sticks',
        paragraphs: [
          'A sustainable week often looks like: three focused reading/video blocks, two question blocks, and one longer Saturday review. Protect one rest half-day so burnout does not erase Ethics retention.',
          'Track hours lightly — enough to know you are on pace for 300 — but track mock trends harder. Rising scores with shrinking review lists beat a perfect calendar that never includes timed practice.',
        ],
      },
      {
        heading: 'Passing is a process score',
        paragraphs: [
          'There is no fixed passing score published. Aim for consistent 70%+ on timed mixed practice with honest review of explanations. If Ethics and FSA are strong and you are guessing on duration and tax, fix those before exam day.',
          'This site is not affiliated with CFA Institute. Use mocks as a readiness check; confirm [registration fees](/cfa-level-1-exam-fees/) (USD 1,140 early / USD 1,490 standard for Level 1) and windows on the official dates & fees page.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'Is 300 hours enough to pass CFA Level 1?',
            a: 'It is the common planning benchmark, not a guarantee. Candidates with weak accounting or limited weekly hours often need more. Pair hours with timed mocks and Ethics/FSA strength.',
          },
          {
            q: 'What order should I study Level 1 topics?',
            a: 'Start with Ethics and Quant foundations, then FSA, then Corporate Issuers / Equity / Fixed Income, then Derivatives, Alternatives, and Portfolio Management. Revisit Ethics in the final month.',
          },
          {
            q: 'When should I start mock exams?',
            a: 'Take a diagnostic timed session early, then full mocks every 2–3 weeks after week 6. Save one strong mock for the final week — not the night before.',
          },
        ],
      },
    ],
    cta: TRIAL_CTA,
    pricingCta: ANNUAL_PRICING_CTA,
  },
  {
    slug: 'cfa-level-1-pass-rate-2026',
    title: 'CFA Level 1 Pass Rate 2026: 39%–45% by Window',
    metaDescription:
      'CFA Level 1 pass rates in 2026: 45% (Feb), 39% (May), 43% (Aug); 10-year average 41%, first-timers 49% in Aug. Results 5–9 weeks after the window closes.',
    h1: 'CFA Level 1 Pass Rate 2026: 45% (Feb), 39% (May), 43% (Aug)',
    published: '2026-08-23',
    updated: '2026-10-28',
    sections: [
      {
        heading: 'CFA Level 1 pass rates by window',
        paragraphs: [
          'CFA Institute’s published Level 1 pass rates for 2026 were 45% in February, 39% in May, and 43% in August. Its 10-year average for Level 1 is 41%. First-time candidates do better than the overall figure: 50% in February 2026 and 49% in August 2026, against 30% and 29% for candidates who had deferred at least once.',
        ],
        table: {
          caption: `Source: CFA Institute [pass rate infographic](${CFA_PASS_RATES_URL}) and [exam page](${CFA_EXAM_URL}) (10-year average), checked 28 Oct 2026. November 2026 results are expected in January 2027.`,
          head: ['Level 1 window', 'Candidates sat', 'Pass rate'],
          rows: [
            ['November 2025', '26,752', '43%'],
            ['February 2026', '24,006', '45%'],
            ['May 2026', '31,566', '39%'],
            ['August 2026', '28,145', '43%'],
            ['10-year average', '—', '41%'],
          ],
        },
        after: [
          'Pass rates are cohort-wide; your outcome depends on preparation, sleep, and execution on the day. Treat them as a reason to study seriously, not to skip mocks. Candidates who under-prepare Ethics and Financial Statement Analysis often fail despite strong Quant scores.',
        ],
      },
      {
        heading: 'How passing works and when results arrive',
        paragraphs: [
          `CFA Institute sets a Minimum Passing Score (MPS) for the exam, and your score report compares your overall scale score with it. Pass or fail is decided on that overall result, so “you need 70%” figures you see online are rules of thumb, not CFA Institute numbers. See CFA Institute’s [exam results page](${CFA_EXAM_RESULTS_URL}) for how scores are reported.`,
          'Results are released within 5–9 weeks after your exam window closes. Check the candidate portal and CFA Institute communications — not rumor threads — for your release date.',
        ],
        bullets: [
          'No penalty for wrong answers — answer every question',
          'Maximum two Level 1 attempts per calendar year; confirm spacing rules officially',
          'A retake is a new registration — see the [cost of a retake](/cfa-level-1-exam-fees/)',
        ],
      },
      {
        heading: 'Benchmark yourself with timed mocks',
        paragraphs: [
          'Use timed practice to see if you finish with time left and whether your review list shrinks week over week. A single mock score is noisy; a trend over four to six sessions is signal.',
          `Start with [${FREE_MOCK.toLowerCase()}](/quiz/session-1) (720+ bank, CBT-style) for a practical readiness check, then unlock Mocks 2–8 with [lifetime access](/pricing). This independent site is not affiliated with CFA Institute.`,
        ],
      },
      {
        heading: 'What strong candidates do differently',
        paragraphs: [
          'They front-load Ethics and FSA, practice calculator speed on Quant and Fixed Income, and review explanations instead of chasing raw question volume. They also schedule early enough to hit early registration pricing when possible.',
        ],
        bullets: [
          'Consistent 70%+ on mixed timed practice is a common personal target',
          'Ethics vignettes weekly in the final month',
          'Sleep and logistics the week of the exam beat last-minute new modules',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'What is the CFA Level 1 pass rate?',
            a: 'CFA Institute published 45% for February 2026, 39% for May 2026, and 43% for August 2026 (43% for November 2025). The 10-year average is 41%. First-time candidates passed at 49% in August 2026.',
          },
          {
            q: 'Is there a minimum passing score?',
            a: 'Yes. CFA Institute sets a Minimum Passing Score (MPS), and your score report shows your overall scale score against it. Rather than chasing a rumoured percentage, aim for strong timed-mock performance and balanced topic coverage.',
          },
          {
            q: 'When do CFA Level 1 results come out?',
            a: 'Within 5–9 weeks after your exam window closes, per CFA Institute’s exam results page. November 2026 results are expected in January 2027.',
          },
          {
            q: 'Does a high mock score guarantee a pass?',
            a: 'No. Mocks are readiness indicators. Exam-day stamina, Ethics accuracy, and avoiding blank answers still matter. Use a trend of mocks, not one lucky score.',
          },
        ],
      },
    ],
    cta: TRIAL_CTA,
    pricingCta: ANNUAL_PRICING_CTA,
  },
  {
    slug: 'cfa-level-1-exam-dates-2026',
    title: 'CFA Level 1 Exam Dates 2026–2027: Feb 22–28, Apply by 5 Nov',
    metaDescription:
      'Next open CFA Level 1 window: 22–28 Feb 2027, register by 5 Nov 2026. Then 11–17 May, 15–22 Aug and 13–19 Nov 2027. 11–17 Nov 2026 registration has closed.',
    h1: 'CFA Level 1 Exam Dates 2026–2027: November 2026 and Every 2027 Window',
    published: '2026-08-23',
    updated: '2026-11-03',
    ctaInHero: true,
    sections: [
      {
        heading: 'November 2026: the last 2026 window (registration closed)',
        paragraphs: [
          'Registering for February 2027? The next open window is 22–28 February 2027, and registration closes 5 November 2026 at 11:59 PM ET (USD 1,490 standard). [February 2027 deadlines & fees →](/blog/cfa-level-1-february-2027-deadlines-fees)',
          'The remaining 2026 CFA® Level 1 exam window is 11–17 November. Registration for it has closed, so if you are not already registered, the next window is February 2027. Registered candidates manage their appointment on CFA Institute’s dates & fees page — this independent site does not sell exam seats.',
          'If you are sitting November, use the next weeks for [timed Mock 1](/quiz/session-1) and weak-topic repair, not for shopping other 2026 windows that have already closed. After November, Level 1 returns in February 2027 (22–28 Feb) — see [February 2027 deadlines & fees](/blog/cfa-level-1-february-2027-deadlines-fees).',
        ],
        bullets: [
          'Upcoming (registration closed): November 2026 — 11–17 Nov',
          'Closed / past: February 2026 — 2–8 Feb',
          'Closed / past: May 2026 — 12–18 May',
          'Closed / past: August 2026 — 18–24 Aug',
        ],
      },
      {
        heading: 'CFA Level 1 exam dates 2027',
        paragraphs: [
          'After the 11–17 Nov 2026 window, CFA Institute lists four Level 1 windows in 2027. February 2027 registration closes 5 November 2026; after that, the next open window is May 2027.',
        ],
        table: {
          caption: `Source: [CFA Institute dates & fees](${CFA_DATES_FEES_URL}), Level I, checked 28 Oct 2026. All deadlines are 11:59 PM ET.`,
          head: ['Level I window', 'Exam dates', 'Registration closes', 'Scheduling deadline'],
          rows: [
            ['Feb 2027', '22–28 Feb 2027', '5 Nov 2026', '10 Nov 2026'],
            ['May 2027', '11–17 May 2027', '10 Feb 2027', '16 Feb 2027'],
            ['Aug 2027', '15–22 Aug 2027', '29 Apr 2027', '6 May 2027'],
            ['Nov 2027', '13–19 Nov 2027', 'Not yet published', 'Not yet published'],
          ],
        },
        after: [
          'The February, May, and August 2027 windows all cost USD 1,140 early or USD 1,490 standard. Only August 2027 still has an early deadline ahead (20 January 2027); registration for it opens 3 November 2026.',
        ],
      },
      {
        heading: 'All 2026 Level 1 windows (for the record)',
        paragraphs: [
          'CFA Institute offered four Level 1 windows in the 2026 cycle. February, May, and August have already passed. November 11–17 is the last 2026 sitting. The 2027 windows and their deadlines are in the table above.',
          'Each window spans several days. After you register for a window, you still must schedule a test-center (or approved) appointment inside it. Miss scheduling and you can lose the fee.',
        ],
      },
      {
        heading: 'Level 2 and Level 3 dates',
        paragraphs: [
          'Level 2 next sits 18–22 November 2026 — see [Level 2 exam dates](/blog/cfa-level-2-exam-dates-2026). Level 3’s next window is 18–21 February 2027 — see [Level 3 exam dates](/blog/cfa-level-3-exam-dates-2026). Cadence differs by level: L2 has no February; L3 has no May/November.',
        ],
      },
      {
        heading: 'Registration vs scheduling',
        paragraphs: [
          'Registration pays for a window; scheduling picks your day and time inside that window. Rescheduling within a window costs USD 250 if appointments are available. For the November 2026 window, the rescheduling deadline is 31 October 2026.',
          'Early registration is cheaper than standard for Level 1: the [registration fee](/cfa-level-1-exam-fees/) is USD 1,140 early or USD 1,490 standard. Verify current pricing on the official site before you pay. Product practice on this site is separate from CFA Institute registration.',
        ],
        bullets: [
          'You may not sit in consecutive windows or within six months of another attempt — confirm rules officially',
          'Maximum two attempts per calendar year',
          'Bring an approved calculator (e.g. BA II Plus or HP 12C)',
        ],
      },
      {
        heading: 'Prep if November is your sitting',
        paragraphs: [
          'Under eight weeks out: finish core readings, then sit complimentary Mock 1 for a baseline score. Spend the following weeks on Ethics, FSA, and your weakest areas — then take additional timed mocks if you have lifetime or monthly access.',
          'Under four weeks: stop heavy new reading. Prioritize Ethics vignettes, FSA ratios, fixed income duration, and full timed sessions. Under one week: light formula review, sleep, and logistics — not new modules.',
          `Try [${FREE_MOCK.toLowerCase()}](/quiz/session-1) here to check pacing before 11–17 November. Mocks 2–8 unlock with [lifetime access](/pricing).`,
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'What are the CFA Level 1 exam dates in 2026?',
            a: 'November 11–17 is the last 2026 window, and its registration has closed. February 2–8, May 12–18, and August 18–24 have already passed. If you are not registered yet, the next window is February 22–28, 2027 (registration closes November 5, 2026). Always confirm on CFA Institute’s dates & fees page before registering.',
          },
          {
            q: 'What are the CFA Level 1 exam dates in 2027?',
            a: 'February 22–28, May 11–17, August 15–22, and November 13–19, 2027. February registration closes November 5, 2026 (schedule by November 10); May registration closes February 10, 2027. November 2027 deadlines are not yet published.',
          },
          {
            q: 'Can I still sit February, May, or August 2026?',
            a: 'No. Those 2026 windows are closed. November 11–17 is the last 2026 Level 1 window listed here.',
          },
          {
            q: 'Is this site affiliated with CFA Institute?',
            a: 'No. This is an independent practice site. Official dates, fees, and policies come only from CFA Institute.',
          },
          {
            q: 'What is the difference between registration and scheduling?',
            a: 'Registration buys your exam window; scheduling books a specific day and time at a test center inside that window. Do both before deadlines.',
          },
          {
            q: 'How much does Level 1 registration cost in 2026?',
            a: 'USD 1,140 early or USD 1,490 standard for Level 1, the same for the February, May, and August 2027 windows. Rescheduling within a window costs USD 250. Confirm current fees on the official CFA Institute site before you pay.',
          },
        ],
      },
    ],
    cta: { label: 'Start free Mock 1 before November', href: '/quiz/session-1' },
    pricingCta: ANNUAL_PRICING_CTA,
  },
  {
    slug: 'cfa-level-1-february-2027-deadlines-fees',
    title: 'CFA Level 1 Feb 2027 Registration: Closes 5 Nov, USD 1,490',
    metaDescription:
      'CFA Level 1 Feb 2027 registration runs 5 May–5 Nov 2026 (11:59 PM ET). Standard fee USD 1,490; early fee closed. Schedule by 10 Nov; exam 22–28 Feb 2027.',
    h1: 'CFA Level 1 February 2027 Registration Dates, Deadlines & Fees',
    published: '2026-10-23',
    updated: '2026-11-03',
    ctaInHero: true,
    heroCta: {
      lead: 'Benchmark before you register.',
      label: 'Try free Mock 1 — 90 timed questions, instant score',
      href: '/quiz/session-1',
    },
    sections: [
      {
        heading: 'When is CFA Level 1 February 2027 registration?',
        paragraphs: [
          `Registration for the February 2027 CFA Level I exam opened 5 May 2026 and closes 5 November 2026 at 11:59 PM ET. The fee is now the USD 1,490 standard fee (the USD 1,140 early fee closed 7 July 2026). Schedule your test appointment by 10 November 2026; the exam window is 22–28 February 2027. Source: [CFA Institute dates & fees](${CFA_DATES_FEES_URL}), checked 3 Nov 2026.`,
          'Compare the annual calendar and build your budget with our [CFA Level 1 fees guide](/cfa-level-1-exam-fees/).',
        ],
        bullets: [
          'Registration window: 5 May – 5 Nov 2026',
          'Registration closes: 5 Nov 2026, 11:59 PM ET',
          'Exam window: 22–28 Feb 2027',
          'Scheduling deadline: 10 Nov 2026',
          'Rescheduling deadline: 11 Feb 2027',
          'Fee: USD 1,490 (standard); no enrollment fee',
        ],
      },
      {
        heading: 'February 2027 Level I timeline',
        paragraphs: ['Every date CFA Institute lists for the February 2027 Level I window:'],
        table: {
          caption: `Source: [CFA Institute dates & fees](${CFA_DATES_FEES_URL}), Level I, February 2027. All deadlines are 11:59 PM ET.`,
          head: ['Date', 'What happens'],
          rows: [
            ['5 May 2026', 'Registration and scheduling opened'],
            ['7 Jul 2026', 'Early registration deadline (USD 1,140) — passed'],
            ['26 Sep 2026', 'Invoice payment deadline — passed'],
            ['5 Nov 2026', 'Registration closes (USD 1,490 standard)'],
            ['10 Nov 2026', 'Scheduling deadline'],
            ['14 Oct / 11 Nov 2026', 'Disability accommodations request deadline: 14 Oct if you registered by 7 Jul (passed), 11 Nov if you registered 8 Jul–5 Nov. Accommodations candidates schedule after CFA Institute decides, so the general scheduling deadline does not apply to them'],
            ['11 Feb 2027', 'Rescheduling deadline'],
            ['22–28 Feb 2027', 'Exam window'],
          ],
        },
        after: [
          'Registration pays for the window; scheduling books your day and time inside it. Do both before the deadlines above.',
        ],
      },
      {
        heading: 'What February 2027 costs',
        paragraphs: [
          `Level I registration for February 2027 is USD 1,490, the standard fee. There is no enrollment fee (CFA Institute dropped the USD 350 fee from 29 April 2025), moving your appointment within the window costs USD 250, and fees exclude local taxes. Full breakdown and budgets: [CFA Level 1 fees guide](/cfa-level-1-exam-fees/).`,
          'Fees for a missed exam appointment are nonrefundable, non-deferrable, and non-transferable, so register for a window your study plan can hit.',
          `Registration buys one sitting. Practice is separate: [free Mock 1](/quiz/session-1) costs nothing; all 8 mocks are [${L1_PRICE_LINE}](/pricing).`,
        ],
      },
      {
        heading: 'If February is too soon: May or August 2027',
        paragraphs: [
          'Both later 2027 windows have the same fees. Only August 2027 still offers the USD 1,140 early fee.',
        ],
        table: L1_2027_WINDOWS_TABLE,
        after: [
          'Sitting Level I in November 2026? CFA Institute does not allow consecutive windows or windows within six months of each other, so February 2027 is not an option for a November 2026 candidate. Check the rule on the official page before you register again.',
        ],
      },
      {
        heading: 'Is February realistic for you?',
        paragraphs: [
          'From late October, February 22 is about 17 weeks away. Against the common 300-hour planning benchmark, that is roughly 18 hours a week, which is tight if you are starting from zero and comfortable if you are already part way through the curriculum.',
          'Benchmark before you register: [take free Mock 1](/quiz/session-1) (90 questions, 135 minutes, instant score), then build a week-by-week schedule with the [300-hour study plan](/blog/how-to-pass-cfa-level-1-study-plan) or the [study plan generator](/study-plan).',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'When is the CFA Level 1 February 2027 registration deadline?',
            a: '5 November 2026 at 11:59 PM ET. The scheduling deadline is 10 November 2026.',
          },
          {
            q: 'When are the CFA Level 1 February 2027 exam dates?',
            a: '22–28 February 2027. You pick a specific day and time inside that window when you schedule.',
          },
          {
            q: 'How much does the February 2027 Level 1 exam cost?',
            a: 'USD 1,490, the standard registration fee, plus any local taxes. There is no enrollment fee. Rescheduling within the window costs USD 250.',
          },
          {
            q: 'Can I still get the USD 1,140 early fee?',
            a: 'Not for February or May 2027: those early deadlines passed on 7 July and 14 October 2026. The next early deadline is 20 January 2027, for the August 2027 window.',
          },
          {
            q: 'Is this site affiliated with CFA Institute?',
            a: 'No. This is an independent practice site. Register, pay, and schedule only through CFA Institute.',
          },
        ],
      },
    ],
    cta: TRIAL_CTA,
    pricingCta: ANNUAL_PRICING_CTA,
  },
  {
    slug: 'cfa-level-1-formulas-2026',
    title: 'CFA Level 1 Formulas 2026: Cheat Sheet & Glossary',
    metaDescription:
      'CFA Level 1 formulas for 2026: 114 searchable entries covering TVM, WACC, duration, CAPM, and ratios. Filter formulas only, then drill free practice next.',
    published: '2026-08-22',
    updated: '2026-08-25',
    sections: [
      {
        heading: 'Why formulas still matter on Level 1',
        paragraphs: [
          'Many items require a quick calculation — NPV, modified duration, put–call parity, or a liquidity ratio from a mini balance sheet. You rarely derive models from scratch, but you must pick the right equation and use your calculator fast.',
          'A searchable [formula glossary](/glossary?formulas=1) beats scattered handwritten sheets when you are reviewing under time pressure in the final month before the [November 2026 window](/blog/cfa-level-1-exam-dates-2026).',
        ],
      },
      {
        heading: 'What is in the glossary',
        paragraphs: [
          '114 formulas across all ten topics, each with a plain definition and see-also links. Use the formulas-only filter for cram sessions; use full glossary mode when you forget what a term means.',
        ],
        bullets: [
          'Quant: TVM, EAR, t-test, regression, portfolio variance',
          'FSA: DuPont, CCC, coverage ratios, turnover days',
          'Fixed income: YTM, Macaulay/modified duration, convexity',
          'Portfolio: CAPM, Sharpe, Treynor, information ratio',
          'Derivatives: put–call parity, forward pricing, binomial hedge ratio',
        ],
      },
      {
        heading: 'How to memorize without wasting hours',
        paragraphs: [
          'Group formulas by decision tree: “Am I discounting cash flows?”, “Am I measuring sensitivity?”, “Am I comparing risk-adjusted return?” Then practice the keystrokes on the BA II Plus until the sequence is automatic.',
          'After a formula pass, immediately do 15–20 related practice questions — or sit [complimentary Mock 1](/quiz/session-1) to apply them under the clock. Recognition without application fails at 90 seconds per question.',
        ],
      },
      {
        heading: 'Exam-day formula habits',
        paragraphs: [
          'Write givens before touching the calculator. Clear TVM between problems. For duration and convexity, watch the sign on yield changes and whether Δy is in decimal or percent form as the stem defines it.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'How many CFA Level 1 formulas should I know?',
            a: 'Focus on high-frequency tools: TVM, ratios, duration/convexity, CAPM/performance ratios, and put–call parity. Our glossary lists 114 entries you can filter for cram sessions.',
          },
          {
            q: 'Can I bring a formula sheet into the exam?',
            a: 'No. Memorize and practice calculator workflows. Use the glossary only while studying.',
          },
          {
            q: 'Should I study formulas before or after topic reading?',
            a: 'Learn the concept first, then lock the formula with practice questions. Pure memorization without stems does not hold under timed conditions.',
          },
        ],
      },
    ],
    cta: { label: 'Open formula glossary', href: '/glossary?formulas=1' },
    pricingCta: ANNUAL_PRICING_CTA,
  },
  {
    slug: 'cfa-level-1-exam-format-topic-weights-2026',
    title: 'CFA Level 1 Exam Format & Topic Weights (2026)',
    metaDescription:
      'CFA Level 1 exam format 2026: 180 questions in two 135-minute sessions, A/B/C choices, topics by session and official weight ranges. Try free Mock Exam 1.',
    published: '2026-08-22',
    updated: '2026-08-25',
    sections: [
      {
        heading: 'Exam structure on exam day',
        paragraphs: [
          '180 multiple-choice questions in two sessions of 90 items and 135 minutes each, on one exam day. Three choices per question (A, B, C). No penalty for guessing — mark an answer for every item.',
          'Session 1: Ethics, Quantitative Methods, Economics, Financial Statement Analysis, Corporate Issuers. Session 2: Equity, Fixed Income, Derivatives, Alternative Investments, Portfolio Management.',
        ],
      },
      {
        heading: '2026 topic weights',
        paragraphs: [
          'Weights are ranges, not exact counts per form. Build your study calendar around the upper end of Ethics and the big three of FSA, Equity, and Fixed Income.',
        ],
        bullets: [
          'Ethical and Professional Standards — 15–20%',
          'Financial Statement Analysis — 11–14%',
          'Equity Investments — 11–14%',
          'Fixed Income — 11–14%',
          'Portfolio Management — 8–12%',
          'Alternative Investments — 7–10%',
          'Quantitative Methods, Economics, Corporate Issuers — 6–9% each',
          'Derivatives — 5–8%',
        ],
      },
      {
        heading: 'Question style tips',
        paragraphs: [
          'Stems often use “most likely” or “least likely.” CFA Institute avoids “except” and “all of the above” style traps. Read whether the vignette assumes IFRS or US GAAP before you calculate.',
          `Complimentary practice questions draw from a 720+ item bank at 2026 weights with CBT-style navigation — useful for pacing before a full 90-question timed mock with lifetime access.`,
        ],
      },
      {
        heading: 'How format should shape your prep',
        paragraphs: [
          'Practice sitting 135 uninterrupted minutes. Flagging and returning is a skill; train it in mocks instead of linear textbook chapter quizzes only. Stamina between sessions also matters — plan nutrition and rest for a full exam day.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'How many questions are on CFA Level 1?',
            a: '180 multiple-choice questions total: two sessions of 90 questions and 135 minutes each on one exam day.',
          },
          {
            q: 'What is the highest-weight topic on Level 1?',
            a: 'Ethical and Professional Standards at 15–20%. FSA, Equity, and Fixed Income follow at 11–14% each.',
          },
          {
            q: 'Is there a penalty for wrong answers?',
            a: 'No. Answer every question. Blank items cannot help you; informed guesses can.',
          },
        ],
      },
    ],
    cta: TRIAL_CTA,
  },
  {
    slug: 'cfa-level-1-ethics-study-guide',
    title: 'CFA Level 1 Ethics Study Guide: Master the 15–20%',
    metaDescription:
      'CFA Level 1 ethics study guide: Standards I-VII, GIPS basics, vignette tactics, and why Ethics is the highest-weight 2026 topic. Practice free questions.',
    published: '2026-08-23',
    updated: '2026-08-25',
    sections: [
      {
        heading: 'Why Ethics is non-negotiable',
        paragraphs: [
          'Ethical and Professional Standards is 15–20% of Level 1 — the largest single topic. Items are applied vignettes: a short story, then what the member should do under the Code and Standards. “Sounds ethical” is not enough; you need the specific Standard.',
          'Strong Ethics scores also protect borderline candidates in CFA Institute’s overall evaluation process historically discussed by candidates — regardless, it is the highest-ROI topic by weight.',
        ],
      },
      {
        heading: 'What to memorize vs apply',
        paragraphs: [
          'Know the structure of Standards I–VII and recurring themes: loyalty to clients, disclosure of conflicts, MNPI, fair dealing, suitability, and GIPS at a firm-composite level. Flashcards for definitions help; exam points come from application drills.',
        ],
        bullets: [
          'MNPI: do not act or cause others to act on material nonpublic information',
          'GIPS: firm-wide, composite construction, fair representation — not product cherry-picking',
          'When two answers seem ethical, pick client-first action with disclosure',
          'Supervisors must have reasonable procedures — ignorance is not a defense',
        ],
      },
      {
        heading: 'Study method that sticks',
        paragraphs: [
          'Read a Standard, then do 20–30 ethics questions immediately. Wrong patterns repeat: gifts, allocation, performance presentation, and supervisor responsibility. Revisit Ethics in the final month even if you aced it early.',
          'Mix Ethics into every timed mock. Session 1 loads Ethics early; fatigue later in the day can still hit if you only practiced it “fresh.”',
          'Try a worked Standard II(A) mosaic-theory question in our [free CFA Level 1 sample questions](/blog/cfa-level-1-practice-questions-free).',
        ],
      },
      {
        heading: 'Common vignette traps',
        paragraphs: [
          'Watch for soft dollar misuse, incomplete performance disclosure, and “everyone does it” rationalizations. If the stem offers a disclosure-plus-client-first option, that is often the winner over a quietly convenient shortcut.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'How much of CFA Level 1 is Ethics?',
            a: 'Ethical and Professional Standards is typically 15–20% of the exam — the largest single topic weight.',
          },
          {
            q: 'Should I study Ethics first or last?',
            a: 'Do a full pass early so vocabulary sticks, then revisit with vignette drills in the final month. Do not leave Ethics only for the last week.',
          },
          {
            q: 'Are Ethics questions calculation-heavy?',
            a: 'Usually no. They are applied reading and judgment under the Code and Standards. Speed comes from recognizing the Standard, not from the calculator.',
          },
        ],
      },
    ],
    cta: { label: 'Ethics study guide lesson', href: '/study-guide/ethical-and-professional-standards' },
  },
  {
    slug: 'cfa-level-1-calculator-ba-ii-plus',
    title: 'CFA Level 1 Calculator: BA II Plus Settings & Tips',
    metaDescription:
      'CFA Level 1 calculator guide: allowed BA II Plus and HP 12C models, P/Y=1 TVM setup, keystrokes and exam-day habits. Practice on our on-screen BA II Plus.',
    published: '2026-08-23',
    updated: '2026-08-25',
    sections: [
      {
        heading: 'Allowed calculators',
        paragraphs: [
          'CFA Institute allows only specific models — commonly the Texas Instruments BA II Plus (including Professional) and Hewlett Packard 12C (including Platinum). Bring spare batteries and a backup calculator if permitted. You cannot share during the exam.',
          'Confirm the current approved list on CFA Institute’s candidate resources before exam day. This independent site is not affiliated with CFA Institute.',
        ],
      },
      {
        heading: 'BA II Plus setup for Level 1',
        paragraphs: [
          'Set P/Y = 1 and C/Y = 1 for most TVM items so I/Y is the per-period rate and N is the number of periods. Clear TVM ([2ND][CLR TVM]) before each new problem. Ordinary annuity is END mode; annuity due uses BEGIN or multiply the ordinary PV by (1+r).',
        ],
        bullets: [
          'Cash outflows (PV, PMT) are often entered as negative',
          'EAR: use the worksheet or (1 + r/m)^m − 1 manually',
          'Practice the same calculator you will bring — no switching week-of',
          'Know how to store intermediate results without losing sign conventions',
        ],
      },
      {
        heading: 'Practice with our on-screen BA II Plus',
        paragraphs: [
          'Every timed mock and topic drill on CFA Mock Exam includes a built-in TI BA II Plus — same TVM keys, P/Y settings, and keystrokes you use on exam day. Tap Calculator at the bottom of the screen while you practice Quant, Fixed Income, and FSA items.',
          'Use it to build muscle memory before your first full timed mock. On the real CBT you must bring an approved physical calculator; the on-screen tool is for practice only.',
        ],
      },
      {
        heading: 'Speed on exam day',
        paragraphs: [
          'Write down givens before touching keys. One mis-keyed N destroys a whole item. For bond duration or IRR-heavy items, know when the question expects a formula shortcut vs full calculator iteration.',
          'Drill Quant and Fixed Income topic drills until TVM sequences feel automatic. Every mock uses the same three-choice format as the live exam.',
        ],
      },
      {
        heading: 'Common BA II Plus mistakes',
        paragraphs: [
          'Leaving P/Y at 12 from a prior problem, forgetting to clear TVM, and mixing BEGIN/END modes are classic score killers. Build a 10-second reset ritual before every new stem.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'Which calculator should I use for CFA Level 1?',
            a: 'Most candidates use the TI BA II Plus or BA II Plus Professional. The HP 12C family is also commonly allowed. Confirm the official approved list before exam day.',
          },
          {
            q: 'What P/Y setting should I use?',
            a: 'For most Level 1 TVM items, set P/Y = 1 and C/Y = 1 so N and I/Y match the per-period definitions in the stem.',
          },
          {
            q: 'Can I use a graphing calculator?',
            a: 'No. Only models on CFA Institute’s approved list are permitted. Do not bring an unapproved device.',
          },
        ],
      },
    ],
    cta: { label: 'Try free Mock 1 with on-screen calculator', href: '/quiz/session-1' },
  },
  {
    slug: 'cfa-level-1-mock-exam-how-to-practice',
    title: 'CFA Mock Exam Tips: Timing, Flagging, Review',
    metaDescription:
      'CFA Level 1 mock exam tips: 90-second pacing, flagging hard items, the CBT navigator and reviewing explanations. Then sit Mock 1 free, no account needed.',
    published: '2026-08-22',
    updated: '2026-10-27',
    sections: [
      {
        heading: 'Pacing for one 90-question session',
        paragraphs: [
          'Budget about 90 seconds per question on average — 135 minutes for 90 items. Flag lengthy FSA vignettes and return after clearing faster Ethics and definition items. Do not spend four minutes on one question in the first hour.',
          `Our timed mocks match this structure with CBT-style flagging from a 720+ item bank at 2026 weights. Start with ${FREE_MOCK.toLowerCase()}; full sittings unlock with lifetime access.`,
        ],
      },
      {
        heading: 'During the mock',
        paragraphs: [
          'Simulate exam conditions: no notes, phone away, same calculator workflow. Use the on-screen BA II Plus (Calculator button) or your physical BA II Plus. Use the navigator to skip and return. Answer every question — no guessing penalty on Level 1.',
        ],
        bullets: [
          'First pass: answer everything you can in 100–110 minutes',
          'Second pass: flagged items and empty blanks',
          'Last 5 minutes: verify nothing blank',
          'Keep water nearby; avoid mid-session phone checks',
        ],
      },
      {
        heading: 'Review workflow that raises the next score',
        paragraphs: [
          'After submit, read explanations for wrong answers and for right answers you guessed. Tag the topic (e.g. “fixed income duration”) and drill that topic before the next full mock.',
          'Rebuild a short error log weekly. If the same trap appears three times, it is a curriculum gap — not bad luck.',
        ],
      },
      {
        heading: 'How many mocks before exam day?',
        paragraphs: [
          'Many candidates sit four to eight full-session equivalents across prep, with increasing realism. Space them so review time exists between sittings. Crashing three mocks in three days teaches little.',
          'For when to start, how to keep an error log and how practice exams fit alongside official CFA Institute practice, see the [CFA practice exam guide](/cfa-practice-exam/).',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'How long should I spend per CFA Level 1 question?',
            a: 'About 90 seconds on average (135 minutes for 90 questions). Flag hard items and return rather than burning four minutes early.',
          },
          {
            q: 'Should I guess on Level 1?',
            a: 'Yes if needed — there is no wrong-answer penalty. Never leave blanks after your second pass.',
          },
          {
            q: 'When should I take my last mock?',
            a: 'Ideally several days before the exam, with light review afterward. Avoid a brutal new mock the night before.',
          },
        ],
      },
    ],
    cta: { label: 'Start a timed mock', href: '/quiz/session-1' },
  },
  {
    slug: 'cfa-level-1-financial-statement-analysis-tips',
    title: 'CFA Level 1 Financial Statement Analysis Tips (2026)',
    metaDescription:
      'CFA Level 1 FSA study tips: ratios, IFRS vs GAAP traps, cash-flow classification, and how to drill the 11-14% topic with free practice questions today.',
    published: '2026-08-23',
    updated: '2026-08-25',
    keywords: ['CFA Level 1 FSA', 'financial statement analysis CFA', 'CFA accounting ratios'],
    sections: [
      {
        heading: 'Why FSA is a score multiplier',
        paragraphs: [
          'Financial Statement Analysis is 11–14% of Level 1 and dense with calculable items: liquidity, activity, profitability, and valuation ratios from mini statements. Items often assume IFRS unless the stem explicitly says US GAAP — read that line before you compute.',
          'FSA also sits in Session 1 with Ethics and Quant, so pacing on long vignettes matters as much as knowing the ratios cold.',
        ],
      },
      {
        heading: 'High-yield FSA checklist',
        paragraphs: [
          'Build a personal checklist you can run mentally in under 20 seconds when a stem drops numbers. Practice until classification (operating vs investing vs financing) is automatic.',
        ],
        bullets: [
          'Cash flow vs accrual: operating / investing / financing classification',
          'Inventory: FIFO vs LIFO effects in rising prices (LIFO not under IFRS)',
          'DuPont ROE: margin × turnover × leverage',
          'Deferred tax assets and liabilities — temporary vs permanent differences',
          'Quality of earnings: receivables growing faster than revenue',
        ],
      },
      {
        heading: 'IFRS vs US GAAP traps',
        paragraphs: [
          'Many Level 1 items default to IFRS. Watch inventory methods, development cost capitalization, and impairment reversals where GAAP differs. If the stem is silent, do not invent US GAAP treatments.',
        ],
      },
      {
        heading: 'How to drill FSA',
        paragraphs: [
          'Pair the FSA study guide lessons with topic drills, then check formulas in the glossary (current ratio, interest coverage, CCC). Full mocks mix FSA with Ethics in session 1 — practice pacing on long vignettes.',
          'After each quiz, rewrite one ratio from memory without looking. If you cannot write it, you do not own it yet.',
          'Try a worked IFRS vs US GAAP cash flow question in our [free CFA Level 1 sample questions](/blog/cfa-level-1-practice-questions-free).',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'How much of Level 1 is Financial Statement Analysis?',
            a: 'Typically 11–14% of the exam — one of the largest calculation-heavy topics after Ethics by weight.',
          },
          {
            q: 'Should I assume IFRS or US GAAP?',
            a: 'Assume IFRS unless the stem explicitly states US GAAP. Always read that line before calculating.',
          },
          {
            q: 'What FSA formulas appear most often?',
            a: 'Liquidity and activity ratios, DuPont ROE, coverage ratios, and cash-flow classification items show up frequently. Use the formula glossary to drill them.',
          },
        ],
      },
    ],
    cta: { label: 'Practice FSA questions', href: '/quiz/financial-reporting-and-analysis' },
  },
  {
    slug: 'cfa-level-1-fixed-income-duration-guide',
    title: 'CFA Level 1 Fixed Income: Duration & Convexity Guide',
    metaDescription:
      'CFA Level 1 fixed income guide: Macaulay & modified duration, convexity, YTM, and price-yield traps. Drill the 11-14% topic with free practice questions.',
    published: '2026-08-23',
    updated: '2026-08-25',
    keywords: ['CFA fixed income', 'duration CFA Level 1', 'bond convexity exam'],
    sections: [
      {
        heading: 'Core fixed income concepts',
        paragraphs: [
          'Fixed Income is 11–14% of Level 1. You must connect price, yield, coupon, and maturity — plus duration as the first-order price sensitivity measure and convexity as the curvature correction when yield moves are large.',
          'Many items are calculator-plus-formula hybrids. Know when modified duration is enough and when convexity or effective duration is required.',
        ],
      },
      {
        heading: 'Formulas to know cold',
        paragraphs: [
          'Memorize the relationships, then practice keystrokes. Sign errors on Δy are the most common avoidable miss.',
        ],
        bullets: [
          'Price = PV of promised cash flows at YTM (or spot rates)',
          'Modified duration ≈ percent ΔP for a parallel yield shift',
          'ΔP/P ≈ −ModDur × Δy + ½ × Convexity × (Δy)²',
          'Current yield = annual coupon / price (not the same as YTM)',
          'Callable bonds: negative convexity when call is likely',
        ],
      },
      {
        heading: 'Exam traps',
        paragraphs: [
          'Clean vs dirty price (accrued interest at settlement). Semiannual coupons mean m = 2 in many US corporate problems. For embedded options, effective duration beats modified duration.',
          'Read whether the yield change is in basis points or percent, and whether the bond is annual or semiannual pay before you punch keys.',
        ],
      },
      {
        heading: 'Study loop for Fixed Income',
        paragraphs: [
          'Read the study guide module, drill topic drills, then verify duration/convexity in the formula glossary. Add Fixed Income items into full timed mocks so Session 2 pacing stays realistic.',
          'Try a worked modified-duration question in our [free CFA Level 1 sample questions](/blog/cfa-level-1-practice-questions-free).',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'What is modified duration on CFA Level 1?',
            a: 'It estimates the percentage price change for a parallel yield shift: %ΔP ≈ −ModDur × Δy (plus a convexity term for larger moves).',
          },
          {
            q: 'When do I use effective duration?',
            a: 'When cash flows may change with yields — especially bonds with embedded options — effective duration is preferred over modified duration.',
          },
          {
            q: 'How much of Level 1 is Fixed Income?',
            a: 'Typically 11–14%, similar to FSA and Equity. It deserves substantial study time and calculator practice.',
          },
        ],
      },
    ],
    cta: { label: 'Practice fixed income questions', href: '/quiz/fixed-income' },
  },
  {
    slug: 'cfa-level-1-quantitative-methods-tvm',
    title: 'CFA Level 1 Quantitative Methods: TVM & Statistics',
    metaDescription:
      'CFA Level 1 quant guide: TVM, EAR, NPV, IRR, hypothesis tests, and regression with calculator tips for the 6-9% weight. Practice free questions today.',
    published: '2026-08-23',
    updated: '2026-08-25',
    keywords: ['CFA quant', 'CFA TVM', 'CFA Level 1 statistics'],
    sections: [
      {
        heading: 'TVM is the foundation',
        paragraphs: [
          'Almost every valuation item is TVM in disguise. Master PV, FV, annuities, and EAR before moving on. Set P/Y = 1 on the BA II Plus; clear TVM between problems.',
          'Weak Quant leaks into FSA discounting, equity valuation, and fixed income pricing. Fix TVM early even though the topic weight is only 6–9%.',
        ],
      },
      {
        heading: 'Stats and tests you will see',
        paragraphs: [
          'Level 1 stats items reward careful reading of sample vs population and which distribution applies. Do not memorize tables you will not have — know decision rules and calculator workflows.',
        ],
        bullets: [
          'Sample variance uses n − 1; know when to use t vs z vs chi-square',
          'Regression: slope = Cov(X,Y)/Var(X); R² is explained variance',
          'Portfolio E(R) and variance with correlation',
          'Bayes and probability rules show up in shorter items',
        ],
      },
      {
        heading: 'NPV, IRR, and decision rules',
        paragraphs: [
          'Know when NPV and IRR conflict, what a crossover rate implies, and how capital rationing changes project ranking. Enter cash flows carefully — one wrong CF0 ruins the item.',
        ],
      },
      {
        heading: 'Study loop',
        paragraphs: [
          'Read the quant study guide module, drill with topic drills, then verify formulas in the glossary. Finish with mixed timed practice so Quant appears under fatigue, not only in fresh morning sessions.',
          'Try a worked monthly-compounding question in our [free CFA Level 1 sample questions](/blog/cfa-level-1-practice-questions-free).',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'How much of CFA Level 1 is Quantitative Methods?',
            a: 'Typically 6–9%. The weight is moderate, but TVM skills support several other topics.',
          },
          {
            q: 'What calculator settings help most for TVM?',
            a: 'P/Y = 1, C/Y = 1 for most items, clear TVM before each problem, and confirm END vs BEGIN for annuities.',
          },
          {
            q: 'Do I need to memorize statistical tables?',
            a: 'Focus on concepts, decision rules, and interpreting given critical values. Practice recognizing which test the stem is asking for.',
          },
        ],
      },
    ],
    cta: { label: 'Practice quant questions', href: '/quiz/quantitative-methods' },
  },
  {
    slug: 'cfa-level-1-results-release-timeline',
    title: 'CFA Level 1 Results Release: Typically 5–7 Weeks',
    metaDescription:
      'CFA Level 1 results release timeline: typically 5-7 weeks after the exam window. Check CFA Institute, not rumors. Keep practicing timed mocks while you wait.',
    published: '2026-08-23',
    updated: '2026-08-25',
    keywords: ['CFA Level 1 results', 'CFA results date', 'when CFA scores release'],
    sections: [
      {
        heading: 'Typical 5–7 week timeline',
        paragraphs: [
          'CFA Level 1 results usually arrive within about six to seven weeks after the exam window closes. CFA Institute announces the release date for each window; check your email and the candidate portal — not third-party guess sites.',
          'For 2026 windows (Feb 2–8, May 12–18, and Aug 18–24 already sat; [Nov 11–17](/blog/cfa-level-1-exam-dates-2026) still ahead), expect a separate results announcement after each sitting. Confirm windows and any published results dates on the official site. This independent site is not affiliated with CFA Institute.',
        ],
      },
      {
        heading: 'What you receive',
        paragraphs: [
          'Candidates get a pass/fail outcome. CFA Institute does not publish your numeric score or a topic-by-topic breakdown to candidates in the same way a mock exam might. If you pass, confirm next-cycle registration and the Practical Skills Module requirements on CFA Institute.',
          'If you do not pass, note attempt limits (maximum two Level 1 attempts per calendar year) and spacing rules before picking the next window.',
        ],
      },
      {
        heading: 'While you wait',
        paragraphs: [
          'If your window is still ahead, keep sitting timed mocks — do not pause study because results lag. Compare mock trends, not single scores, against the pass rate context in our pass rate guide.',
          'After you sit, light review is optional; rest matters too. Avoid doom-scrolling unofficial “results leaked” posts.',
        ],
      },
      {
        heading: 'Practical Skills Module reminder',
        paragraphs: [
          'Complete required Practical Skills Module obligations on the timeline CFA Institute specifies for your registration. Results release can interact with PSM completion requirements — verify your checklist in the candidate portal.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'How long after CFA Level 1 until results?',
            a: 'Typically about 5–7 weeks after the exam window closes. CFA Institute publishes the exact release date for each window.',
          },
          {
            q: 'Will I see a numeric score?',
            a: 'Candidates generally receive pass/fail rather than a detailed numeric score like a practice mock. Rely on official portal communications.',
          },
          {
            q: 'Where should I check for results?',
            a: 'Your CFA Institute candidate portal and official email — not social media rumor threads or unofficial countdown sites.',
          },
        ],
      },
    ],
    cta: { label: 'Benchmark with a mock', href: '/quiz/session-1' },
  },
  {
    slug: 'cfa-level-1-alternative-investments-guide',
    title: 'CFA Level 1 Alternative Investments Study Guide (2026)',
    metaDescription:
      'CFA Level 1 alternative investments: PE, hedge funds, real estate, commodities, fees & MOIC. Master the 7-10% topic with free practice question drills.',
    published: '2026-08-23',
    updated: '2026-08-25',
    keywords: ['CFA alternatives', 'CFA Level 1 hedge funds', 'alternative investments exam'],
    sections: [
      {
        heading: 'Topic scope and weight',
        paragraphs: [
          'Alternatives are 7–10% of Level 1 — larger than Derivatives. Know structures (LP/GP), fee terms (management, incentive, hurdle, high-water mark), and return metrics (IRR, MOIC, TVPI).',
          'Items often test definitions and fee math rather than forecasting crypto prices. Focus on structures, risks, and performance reporting.',
        ],
      },
      {
        heading: 'High-yield areas',
        paragraphs: [
          'Build a one-page map of asset classes and the one metric each class emphasizes on Level 1. Then drill until fee waterfalls and roll yield feel routine.',
        ],
        bullets: [
          'Private equity: J-curve, capital calls, waterfalls',
          'Hedge funds: soft vs hard hurdle; fee net of management fee',
          'Commodities: roll yield in contango vs backwardation',
          'Real estate / infrastructure: core vs value-add vs opportunistic',
          'Digital assets: custody and operational risk (not price predictions)',
        ],
      },
      {
        heading: 'Fee and performance traps',
        paragraphs: [
          'Read whether the incentive fee is calculated before or after the management fee, and whether the hurdle is soft or hard. High-water marks prevent re-paying incentive fees on recovered losses — know the vocabulary cold.',
        ],
      },
      {
        heading: 'How to practice',
        paragraphs: [
          'Use the Alternatives study guide lesson, then topic drills. Include Alternatives in full Session 2–style timed practice so it does not get skipped after Fixed Income fatigue.',
          'For worked examples in the exam’s three-choice format, start with our [free CFA Level 1 sample questions](/blog/cfa-level-1-practice-questions-free).',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'How much of Level 1 is Alternative Investments?',
            a: 'Typically 7–10% — larger than Derivatives. Do not treat it as an optional footnote.',
          },
          {
            q: 'What is a high-water mark?',
            a: 'A high-water mark means incentive fees are not charged again until the fund exceeds its previous peak — preventing double-charging on recovery.',
          },
          {
            q: 'Do I need to predict crypto prices for Level 1?',
            a: 'No. Focus on operational and custody risk concepts, not market calls.',
          },
        ],
      },
    ],
    cta: { label: 'Practice alternatives questions', href: '/quiz/alternative-investments' },
  },
  {
    slug: 'cfa-level-1-derivatives-study-guide',
    title: 'CFA Level 1 Derivatives: Options, Forwards & Parity',
    metaDescription:
      'CFA Level 1 derivatives study guide: forwards, futures, swaps, moneyness, put-call parity and the binomial model. Nail the 5–8% weight with topic drills.',
    published: '2026-08-23',
    updated: '2026-08-25',
    keywords: ['CFA derivatives', 'put call parity CFA', 'CFA Level 1 options'],
    sections: [
      {
        heading: 'What Level 1 expects',
        paragraphs: [
          'Derivatives is 5–8% but highly formulaic: moneyness, intrinsic value, put–call parity, forward pricing, and one-period binomial hedging. You will not build full BSM on the exam, but you must know inputs and parity relationships.',
          'Because the topic is compact, a short intensive week with heavy question volume can move your score more than another pass of a familiar FSA chapter.',
        ],
      },
      {
        heading: 'Must-know identities',
        paragraphs: [
          'Write each identity once from memory daily during your Derivatives week, then immediately solve three stems that use it.',
        ],
        bullets: [
          'Call intrinsic = max(S − K, 0); put intrinsic = max(K − S, 0)',
          'European parity: c + PV(K) = p + S (adjust for dividends as taught)',
          'Forward price ≈ spot grown at r minus benefits plus costs',
          'Binomial hedge ratio h = (C_u − C_d)/(S_u − S_d)',
        ],
      },
      {
        heading: 'Forwards, futures, and swaps at Level 1 depth',
        paragraphs: [
          'Know marking to market for futures, counterparty differences vs forwards, and the basic purpose of interest rate and currency swaps. Level 1 rarely requires multi-period swap valuation from scratch.',
        ],
      },
      {
        heading: 'Practice approach',
        paragraphs: [
          'Drill topic drills until parity and moneyness are automatic, then mix Derivatives into full timed sessions. Pair with the formula glossary for put–call parity and forward pricing.',
          'For worked examples in the exam’s three-choice format, start with our [free CFA Level 1 sample questions](/blog/cfa-level-1-practice-questions-free).',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'How much of CFA Level 1 is Derivatives?',
            a: 'Typically 5–8%. Smaller weight, but high formula density — easy points if practiced.',
          },
          {
            q: 'Do I need Black–Scholes on Level 1?',
            a: 'You need intuition about inputs and parity relationships more than a full BSM derivation. Focus on intrinsic value, parity, forwards, and one-period binomial.',
          },
          {
            q: 'What is put–call parity used for?',
            a: 'It links European call and put prices with the underlying and PV of strike — used to spot mispricing and to solve for a missing variable in stems.',
          },
        ],
      },
    ],
    cta: { label: 'Practice derivatives questions', href: '/quiz/derivatives' },
  },
  {
    slug: 'cfa-level-1-portfolio-management-capm',
    title: 'CFA Level 1 Portfolio Management: CAPM & Risk Metrics',
    metaDescription:
      'CFA Level 1 portfolio management: CAPM, Sharpe, Treynor, information ratio, and IPS basics. Study the 8-12% topic and drill with free practice questions.',
    published: '2026-08-23',
    updated: '2026-08-25',
    keywords: ['CFA CAPM', 'CFA portfolio management', 'CFA Level 1 Sharpe ratio'],
    sections: [
      {
        heading: 'Risk and return toolkit',
        paragraphs: [
          'Portfolio Management is 8–12% of Level 1. Know systematic vs unsystematic risk, diversification, CAPM, and performance ratios. Beta is the regression slope of asset returns on market returns.',
          'Many items are direct formula applications — free points if you memorize cleanly and watch whether the stem gives σ or β in the denominator.',
        ],
      },
      {
        heading: 'Key formulas',
        paragraphs: [
          'Practice each ratio with two numeric stems: one clean, one with a distractor that swaps σ and β. That single habit prevents Treynor/Sharpe mix-ups.',
        ],
        bullets: [
          'CAPM: E(R_i) = R_f + β_i [E(R_m) − R_f]',
          'Sharpe: (R_p − R_f) / σ_p',
          'Treynor: (R_p − R_f) / β_p',
          'Information ratio: active return / tracking error',
          'Portfolio β = weighted sum of asset betas',
        ],
      },
      {
        heading: 'IPS and behavior',
        paragraphs: [
          'IPS constraints (time, tax, liquidity, legal, unique) and behavioral biases appear as conceptual items — pair reading with ethics-style careful reading of vignettes.',
          'Know the difference between risk tolerance language and hard constraints that force portfolio changes.',
        ],
      },
      {
        heading: 'How to drill Portfolio Management',
        paragraphs: [
          'Use the study guide module, formula glossary filter, and topic drills. Include PM in Session 2 timed practice so CAPM items appear when you are tired.',
          'Try a worked CAPM expected-return question in our [free CFA Level 1 sample questions](/blog/cfa-level-1-practice-questions-free).',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'How much of Level 1 is Portfolio Management?',
            a: 'Typically 8–12%. It is larger than Quant or Derivatives and deserves dedicated formula practice.',
          },
          {
            q: 'What is the difference between Sharpe and Treynor?',
            a: 'Sharpe divides excess return by total volatility (σ); Treynor divides excess return by beta (systematic risk).',
          },
          {
            q: 'What does CAPM say about expected return?',
            a: 'Expected return rises linearly with beta: E(R_i) = R_f + β_i × market risk premium.',
          },
        ],
      },
    ],
    cta: { label: 'Portfolio management drill', href: '/quiz/portfolio-management-and-wealth-planning' },
  },
  {
    slug: 'cfa-level-1-last-month-study-plan',
    title: 'CFA Level 1 Last-Month Study Plan (Final 4 Weeks)',
    metaDescription:
      'CFA Level 1 final-month study plan: week by week for the last 4 weeks, covering mocks, Ethics, formulas and what to stop studying. Try free Mock Exam 1 today.',
    published: '2026-08-23',
    updated: '2026-08-26',
    keywords: ['CFA last month', 'CFA cram plan', 'CFA Level 1 final weeks'],
    sections: [
      {
        heading: 'Week 4 before exam',
        paragraphs: [
          'Full timed mock under exam conditions (90Q / 135 min mirrors one session) when you have [lifetime access](/pricing) — or sit [complimentary Mock 1](/quiz/session-1) first for a baseline score. Review misses with explanations and the topic breakdown when paid. List weak topics by weight — fix FSA and Ethics gaps first.',
          'Confirm your appointment, ID, and approved calculator. For the remaining [November 11–17 2026 window](/blog/cfa-level-1-exam-dates-2026), double-check logistics early — confirm dates on CFA Institute’s site.',
        ],
      },
      {
        heading: 'Weeks 3–2',
        paragraphs: [
          'Topic drills on weak areas only. Second full mock mid-period. Formula pass using the glossary formulas filter. Re-read ethics standards summaries and do 30+ ethics questions.',
        ],
        bullets: [
          'No new curriculum modules unless critical gap',
          'Sleep and exercise — cognitive load matters',
          'Confirm test center logistics and ID requirements',
          'Keep one rest evening per week to avoid burnout',
        ],
      },
      {
        heading: 'Final week',
        paragraphs: [
          'Light review only: formula sheet, ethics, calculator practice. One short mixed quiz optional — not a brutal new mock three days before. Rest the day before the exam.',
          'Pack calculator, spare batteries, and required ID the night before. Know travel time to the center.',
        ],
      },
      {
        heading: 'What to stop studying',
        paragraphs: [
          'Stop opening brand-new optional readings. Stop rewriting entire notes. Stop comparing yourself to anonymous forum study hours. Convert anxiety into one more Ethics vignette set or duration drill.',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'Can I still pass CFA Level 1 with one month left?',
            a: 'If foundations exist, a focused month of mocks, Ethics, FSA, and formulas can help. If you have barely started, confirm whether November 11–17 is still realistic — or wait for the next published cycle on CFA Institute’s dates page.',
          },
          {
            q: 'How many mocks in the last month?',
            a: 'Typically two to four session-length timed practices with full review between them — quality of review beats stacking mocks back-to-back.',
          },
          {
            q: 'Should I study new topics in the final week?',
            a: 'Only for critical high-weight gaps. Prefer Ethics, formulas, and light mixed review over new modules.',
          },
        ],
      },
    ],
    cta: { label: 'Sit a timed mock now', href: '/quiz/session-1' },
  },
  {
    slug: 'cfa-level-1-vs-level-2-difference',
    title: 'CFA Level 1 vs Level 2: Format, Difficulty & Pass Rate',
    metaDescription:
      'CFA Level 1 vs Level 2: standalone multiple choice vs vignette item sets, 2 × 135 vs 2 × 132 minutes, and a 10-year average pass rate of 41% vs 45%.',
    published: '2026-08-23',
    updated: '2026-10-28',
    keywords: ['CFA Level 1 vs Level 2', 'CFA Level 2 format', 'after passing Level 1'],
    sections: [
      {
        heading: 'Level 1 vs Level 2 at a glance',
        paragraphs: [
          'Level 1 asks standalone multiple choice questions; Level 2 asks multiple choice questions grouped under vignette item sets. Level 2 sessions are slightly shorter and its 10-year average pass rate is higher, but every Level 2 candidate has already passed Level 1, so the two rates are not a like-for-like difficulty score.',
        ],
        table: {
          caption: `Source: CFA Institute [exam page](${CFA_EXAM_URL}), checked 28 Oct 2026.`,
          head: ['Exam feature', 'Level 1', 'Level 2'],
          rows: [
            ['Question format', 'Standalone multiple choice', 'Vignette item sets (multiple choice)'],
            ['Exam time', '2 × 135 minutes', '2 × 132 minutes'],
            ['10-year average pass rate', '41%', '45%'],
          ],
        },
        after: [
          'Recent windows: Level 1 passed 45% in February, 39% in May, and 43% in August 2026; Level 2 passed 43% in May and 45% in August 2026. See the [CFA Level 1 pass rate](/blog/cfa-level-1-pass-rate-2026) breakdown.',
        ],
      },
      {
        heading: 'Format change',
        paragraphs: [
          'Level 1 is standalone multiple-choice (180 items, two sessions). Level 2 uses item sets — vignettes with multiple related multiple-choice questions each, still two sessions. Depth and integration increase; recall alone is not enough.',
          'If you are still on Level 1, train CBT pacing with 90-question timed sessions now. That stamina transfers even though Level 2 stems are longer.',
        ],
      },
      {
        heading: 'Topic emphasis shifts',
        paragraphs: [
          'Ethics remains heavy. Level 2 weights more toward asset valuation, financial reporting application, and portfolio management. Level 1 builds the vocabulary; Level 2 applies it in longer cases.',
          'Strong Level 1 FSA and Equity foundations make Level 2 less punishing. Weak Level 1 Accounting often becomes a Level 2 bottleneck.',
        ],
      },
      {
        heading: 'After you pass Level 1',
        paragraphs: [
          'Confirm your Practical Skills Module is complete for results release. Plan the next window you can sit (remember the twice-per-year and non-consecutive window rules — confirm officially). When you are ready for Level 2, use dedicated [item-set mocks](/blog/cfa-level-2-mock-exam-2026) — Level 1 sittings alone will not train vignette pacing.',
          `Windows differ by level: after November 2026, Level 2 next sits 18–22 May 2027. Level 1 and Level 2 registration both cost USD 1,140 early or USD 1,490 standard — confirm on CFA Institute’s [dates & fees page](${CFA_DATES_FEES_URL}). This site is not affiliated with CFA Institute.`,
        ],
      },
      {
        heading: 'Should you think about Level 2 during Level 1?',
        paragraphs: [
          'Lightly: learn Level 1 deeply enough that Level 2 cases feel like applications, not a new language. Do not abandon Level 1 mocks to skim Level 2 early if your Level 1 window is weeks away. After you pass, switch to [Level 2 topic weights](/blog/cfa-level-2-topic-weights-2026) and a [Level 2 study plan](/blog/how-to-pass-cfa-level-2-study-plan).',
        ],
      },
      {
        heading: 'Frequently asked questions',
        faqs: [
          {
            q: 'What is the biggest difference between Level 1 and Level 2?',
            a: 'Level 1 is mostly standalone multiple-choice; Level 2 uses vignette-based item sets that require deeper application across longer cases.',
          },
          {
            q: 'Which is harder, Level 1 or Level 2?',
            a: 'Difficulty differs in kind rather than by an official ranking: Level 1 tests breadth with standalone questions, Level 2 tests depth with vignettes. On the numbers, CFA Institute’s 10-year average pass rate is 41% for Level 1 and 45% for Level 2, but Level 2 candidates have all passed Level 1 already, so a higher pass rate does not by itself mean an easier exam.',
          },
          {
            q: 'Does Ethics still matter on Level 2?',
            a: 'Yes. Ethics remains a major topic across levels. Keep vignette skills sharp after you pass Level 1.',
          },
          {
            q: 'When can I sit Level 2 after Level 1?',
            a: 'Follow CFA Institute attempt-spacing and calendar-year limits. Confirm eligible windows on the official site after you pass and complete required modules.',
          },
        ],
      },
    ],
    cta: L2_PRICING_CTA,
  },
];

export function inferBlogLevel(slug, explicit) {
  if (explicit === 'l1' || explicit === 'l2' || explicit === 'l3') return explicit;
  const s = String(slug || '').toLowerCase();
  if (s.includes('level-3') || s.includes('vs-level-3')) return 'l3';
  if (s.includes('level-2') || s.includes('vs-level-2')) return 'l2';
  return 'l1';
}

export function getBlogPost(slug) {
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return null;
  return {
    ...post,
    keywords: post.keywords || BLOG_KEYWORDS[slug] || [],
    level: inferBlogLevel(slug, post.level),
  };
}

export function getBlogPostsByLevel(level) {
  const want = String(level || '').toLowerCase();
  return BLOG_POSTS.map((p) => getBlogPost(p.slug)).filter((p) => p && p.level === want);
}

export function getRelatedPosts(slug, limit = 4) {
  const slugs = BLOG_RELATED_SLUGS[slug] || BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, limit).map((p) => p.slug);
  return slugs
    .map((s) => getBlogPost(s))
    .filter(Boolean)
    .slice(0, limit);
}

/** Trailing-slash canonical (src/seo/urls.js) — the URL Netlify serves with 200. */
export function blogPostUrl(slug) {
  return `https://cfamockexam.com/blog/${slug}/`;
}

export function allBlogSitemapUrls() {
  return BLOG_POSTS.map((p) => blogPostUrl(p.slug));
}

/** Adjacent posts in search-priority order (prev / next navigation + SEO). */
export function getBlogPostNeighbors(slug) {
  const index = BLOG_POSTS.findIndex((p) => p.slug === slug);
  if (index < 0) return { prev: null, next: null };
  return {
    prev: index > 0 ? getBlogPost(BLOG_POSTS[index - 1].slug) : null,
    next: index < BLOG_POSTS.length - 1 ? getBlogPost(BLOG_POSTS[index + 1].slug) : null,
  };
}
